import { useEffect, useMemo, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Play, CheckCircle, Trash2, FileText, UserPlus, Bike as BikeIcon, Wrench, Package, Printer, Camera, MessageSquare, X, FileDown, Pencil, Minimize2, Banknote, IndianRupee } from "lucide-react";
import { useMinimized } from "@/context/MinimizedContext";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";
import { SearchCombobox } from "@/components/SearchCombobox";

const statusColor = {
  open: "text-blue-400 bg-blue-500/10",
  in_progress: "text-yellow-500 bg-yellow-500/10",
  completed: "text-green-500 bg-green-500/10",
  delivered: "text-primary bg-primary/10",
  cancelled: "text-red-500 bg-red-500/10",
};

const emptyForm = { customer_id: "", vehicle_id: "", complaints: "", mechanic_id: "", store_id: "", next_service_date: "", job_type: "", job_date: "",
  inst_name: "", inst_mobile: "", inst_vehicle: "", inst_services: [], inst_rate: "", inst_remarks: "", inst_calc: "inclusive" };

const INSTANT_PAY_MODES = [["cash", "Cash"], ["phonepay", "PhonePay"], ["paytm", "Paytm"], ["upi", "Other UPI"], ["card", "Card"], ["credit", "Credit"]];

const MODIFY_STATUSES = ["completed", "pending_payment", "invoice_generated", "challan_generated", "delivered"];

function InstantPayDialog({ ctx, onClose }) {
  const [rows, setRows] = useState([{ amount: "", mode: "cash" }]);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [history, setHistory] = useState([]);
  const [livePaid, setLivePaid] = useState(null);
  const [origHist, setOrigHist] = useState([]);
  useEffect(() => {
    setHistory([]);
    setLivePaid(null);
    if (!ctx) return;
    setRows([{ amount: String(ctx.invoice?.due ?? ctx.invoice?.total ?? ""), mode: "cash" }]);
    setPaid(false);
    if (ctx.invoice?.id) api.get(`/invoices/${ctx.invoice.id}/payments`).then(r => { setHistory(r.data || []); setOrigHist(r.data || []); }).catch(() => {});
  }, [ctx]);
  if (!ctx) return null;
  const inv = ctx.invoice;
  const due = Number(livePaid?.due ?? inv?.due ?? inv?.total ?? 0);
  const dirtyRows = history.filter(p => { const o = origHist.find(x => x.id === p.id); return o && (Number(o.amount) !== Number(p.amount) || o.method !== p.method); });
  const paidSoFar = Number(livePaid?.paid ?? inv?.paid ?? 0);
  const newTotal = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const updHist = (id, patch) => setHistory(h => h.map(p => p.id === id ? { ...p, ...patch } : p));
  const saveHist = async (p) => {
    const amt = Number(p.amount);
    if (amt < 0) { toast.error("Amount cannot be negative"); return; }
    try {
      const { data } = await api.patch(`/payments/${p.id}`, { amount: amt, method: p.method });
      setLivePaid(data);
      setOrigHist(h => h.map(o => o.id === p.id ? { ...o, amount: amt, method: p.method } : o));
      toast.success("Payment updated — totals recalculated");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const printReceipt = async () => {
    try {
      const res = await fetch(`${API}/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF generation failed");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const setRow = (i, patch) => setRows(x => x.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = () => {
    const remaining = Math.max(0, Math.round((due - newTotal) * 100) / 100);
    setRows(x => [...x, { amount: remaining ? String(remaining) : "", mode: "cash" }]);
  };
  const delRow = (i) => setRows(x => x.filter((_, j) => j !== i));
  const collect = async () => {
    const valid = rows.filter(r => Number(r.amount) > 0 && r.mode !== "credit");
    const creditAmt = rows.filter(r => r.mode === "credit").reduce((a, r) => a + (Number(r.amount) || 0), 0);
    if (!valid.length && !(creditAmt > 0) && !dirtyRows.length) { toast.success("Saved — no payment changes to record"); onClose(); return; }
    if (valid.reduce((a, r) => a + Number(r.amount), 0) > due + 0.01) { toast.error(`Amount exceeds balance due ${inr(due)}`); return; }
    if (!valid.length && !dirtyRows.length && creditAmt > 0) {
      toast.success("Marked as credit — invoice due, job card stays Pending Payment");
      onClose(); return;
    }
    setBusy(true);
    try {
      let last = null;
      for (const p of dirtyRows) {
        const { data } = await api.patch(`/payments/${p.id}`, { amount: Number(p.amount), method: p.method });
        last = data;
      }
      if (dirtyRows.length) setOrigHist(h => h.map(o => { const p = dirtyRows.find(d => d.id === o.id); return p ? { ...o, amount: Number(p.amount), method: p.method } : o; }));
      for (const r of valid) {
        const { data } = await api.post("/payments", { invoice_id: inv.id, amount: Number(r.amount), method: r.mode });
        last = data;
      }
      if (creditAmt > 0) toast.success(`Credit portion ${inr(creditAmt)} kept as balance due`);
      if (last?.invoice_status === "paid") { setPaid(true); toast.success("Payment collected — job card Completed & Paid"); }
      else { toast.success(`Payment recorded — balance due ${inr(last?.due ?? 0)}`); onClose(); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ctx} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-md" onKeyDownCapture={handleSimpleFormNav}>
        <DialogHeader><DialogTitle className="font-display uppercase">{paid ? "Payment Collected" : "Collect Payment"}</DialogTitle></DialogHeader>
        <div className="space-y-3" data-testid="instant-pay-dialog">
          <div className="flex justify-between items-baseline border-b border-border pb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Invoice</div>
              <div className="font-mono text-xs" data-testid="inst-pay-invoice">{inv.invoice_number}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1" data-testid="inst-pay-tax">{(inv.calc_type || "inclusive") === "inclusive" ? `incl. GST ${inr(inv.gst_amount || 0)}` : `+ GST ${inr(inv.gst_amount || 0)}`}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Balance Due</div>
              <div className="font-display font-black text-2xl text-primary" data-testid="inst-pay-due">{inr(due)}</div>
              <div className="text-[10px] font-mono text-muted-foreground" data-testid="inst-pay-total">Total {inr(inv.total)} · Paid {inr(paidSoFar)}</div>
            </div>
          </div>
          {history.length > 0 && !paid && (
            <div className="border border-border rounded-sm p-2" data-testid="inst-pay-history">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Already Collected (editable)</div>
              {history.map((p, i) => (
                <div key={p.id} data-testid={`inst-pay-hist-${i}`} className="grid grid-cols-[70px_1fr_110px_24px] gap-2 items-center text-xs font-mono border-t border-border/50 py-1">
                  <span className="text-muted-foreground">{(p.created_at || "").slice(0, 10)}</span>
                  <Input type="number" min="0" data-testid={`inst-hist-amount-${i}`} value={p.amount} onChange={(e) => updHist(p.id, { amount: e.target.value })} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <Select value={INSTANT_PAY_MODES.some(([v]) => v === p.method) && p.method !== "credit" ? p.method : "cash"} onValueChange={(v) => updHist(p.id, { method: v })}>
                    <SelectTrigger data-testid={`inst-hist-mode-${i}`} className="h-7 rounded-sm bg-secondary border-border text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>{INSTANT_PAY_MODES.filter(([v]) => v !== "credit").map(([v, l]) => <SelectItem key={v} value={v} data-testid={`inst-hist-mode-${i}-${v}`}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" data-testid={`inst-hist-save-${i}`} onClick={() => saveHist(p)} className="text-green-500 justify-self-end" title="Save payment"><CheckCircle className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          )}
          {paid ? (
            <div className="space-y-3">
              <div className="text-xs text-green-500 uppercase tracking-wider" data-testid="inst-pay-success">Payment recorded · Job card marked Completed &amp; Paid</div>
              <div className="flex gap-2">
                <Button data-testid="inst-print-receipt" onClick={printReceipt} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/> Print Receipt</Button>
                <Button data-testid="inst-pay-close" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.15em] text-xs">Close</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Collect (split across modes allowed)</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="inst-pay-add-split" onClick={addRow} className="h-6 rounded-sm text-[10px] uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Split</Button>
                </div>
                {rows.map((r, i) => (
                  <div key={i} data-testid={`inst-split-row-${i}`} className="grid grid-cols-[1fr_130px_28px] gap-2 items-center">
                    <Input data-testid={`inst-split-amount-${i}`} type="number" min="0" placeholder="Amount" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} className="h-8 rounded-sm bg-secondary border-border font-mono text-right"/>
                    <Select value={r.mode} onValueChange={(v) => setRow(i, { mode: v })}>
                      <SelectTrigger data-testid={`inst-split-mode-${i}`} className="h-8 rounded-sm bg-secondary border-border text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{INSTANT_PAY_MODES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`inst-split-mode-${i}-${v}`}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" data-testid={`inst-split-del-${i}`} onClick={() => delRow(i)} disabled={rows.length === 1} className="text-destructive justify-self-end disabled:opacity-30"><X className="w-3.5 h-3.5"/></button>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-mono border-t border-border pt-2" data-testid="inst-pay-new-total-row">
                  <span className="text-muted-foreground uppercase tracking-wider text-[10px]">Collecting now</span>
                  <span className={newTotal > due ? "text-destructive" : "text-primary"} data-testid="inst-pay-new-total">{inr(newTotal)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="inst-collect-btn" onClick={collect} disabled={busy} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">{busy ? "Saving…" : "Collect Payment"}</Button>
                <Button data-testid="inst-skip-btn" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.15em] text-xs">Collect Later</Button>
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Credit keeps the job card in Pending Payment until collected</div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpSection({ detail, onSaved }) {
  const [days, setDays] = useState("");
  const [km, setKm] = useState("");
  const [busy, setBusy] = useState(false);
  const fmtD = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return "—"; }
  };
  const applyAll = async () => {
    const d = parseInt(days, 10);
    const k = parseFloat(km);
    if ((!d || d <= 0) && (!k || k <= 0)) { toast.error("Enter days and/or odometer KM"); return; }
    const patch = {};
    if (d > 0) {
      const base = new Date(detail.created_at);
      base.setDate(base.getDate() + d);
      patch.next_service_date = base.toISOString().slice(0, 10);
    }
    if (k > 0) {
      const cur = Number(detail.odometer || 0);
      if (!cur) { toast.error("No current odometer reading on this job card"); return; }
      patch.next_service_odometer = Math.round(cur + k);
    }
    setBusy(true);
    try {
      await api.patch(`/jobcards/${detail.id}/next-service`, patch);
      const done = [];
      if (patch.next_service_date) done.push(`due date ${fmtD(patch.next_service_date)}`);
      if (patch.next_service_odometer) done.push(`due at ${patch.next_service_odometer.toLocaleString("en-IN")} KM`);
      toast.success(`Next service ${done.join(" · ")} saved`);
      onSaved?.(patch);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-sm p-4" data-testid="completion-details-section">
      <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 mb-3"><CheckCircle className="w-3 h-3"/> Follow Up</div>
      <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground whitespace-nowrap">Next Service Due:</div>
        <Input data-testid="fu-days-input" type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} placeholder="Days" className="rounded-sm bg-secondary border-border w-20 h-8 text-xs"/>
        <div className="text-[10px] text-muted-foreground whitespace-nowrap">Days</div>
        <Input data-testid="fu-odo-input" type="number" min="1" value={km} onChange={(e) => setKm(e.target.value)} placeholder="KM" className="rounded-sm bg-secondary border-border w-28 h-8 text-xs"/>
        <div className="text-[10px] text-muted-foreground whitespace-nowrap">Odometer KM</div>
        <Button size="sm" data-testid="fu-apply-btn" onClick={applyAll} disabled={busy} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-[9px] h-8 px-2.5">Apply</Button>
        <div className="border border-border bg-secondary rounded-sm px-2 h-8 flex items-center font-mono text-[11px] min-w-24 whitespace-nowrap" data-testid="detail-next-service-due" title="Calculated due date (read-only)">{fmtD(detail.next_service_date)}</div>
        <div className="border border-border bg-secondary rounded-sm px-2 h-8 flex items-center font-mono text-[11px] min-w-24 whitespace-nowrap" data-testid="detail-next-service-odometer" title="Calculated due odometer (read-only)">{detail.next_service_odometer ? `${Number(detail.next_service_odometer).toLocaleString("en-IN")} KM` : "—"}</div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 uppercase tracking-wider">Due date flows to the Follow Up Reminder list as this customer's Next Due Date</div>
    </div>
  );
}
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const displayStatusColor = {
  Open: "text-blue-400 bg-blue-500/10",
  Pending: "text-blue-400 bg-blue-500/10",
  "Pending Payment": "text-orange-400 bg-orange-500/10",
  "In Progress": "text-yellow-500 bg-yellow-500/10",
  "Challan Generated": "text-cyan-400 bg-cyan-500/10",
  "Invoice Generated": "text-purple-400 bg-purple-500/10",
  Completed: "text-green-500 bg-green-500/10",
  Cancelled: "text-red-500 bg-red-500/10",
  Deleted: "text-red-400 bg-red-500/10 line-through",
};
const emptyCust = { name: "", phone: "", address: "" };

const PAY_STATUS = {
  paid: { label: "Paid", cls: "text-green-500 bg-green-500/10 border-green-500/30" },
  unpaid: { label: "Pending Payment", cls: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  partial: { label: "Partially Paid", cls: "text-yellow-500 bg-yellow-500/10 border-yellow-500/30" },
  revoked: { label: "Revoked", cls: "text-red-500 bg-red-500/10 border-red-500/30" },
};

const jobTaxInfo = (d) => {
  const sub = d?.subtotal || 0;
  const rate = d?.tax_rate ?? 18;
  const ct = d?.calc_type || "inclusive";
  const tax = d?.tax_amount ?? (ct === "exclusive" ? sub * rate / 100 : (rate ? sub - sub / (1 + rate / 100) : 0));
  const grand = d?.grand_total ?? (ct === "exclusive" ? sub + tax : sub);
  return { ct, rate, tax, grand, sub };
};

const instTaxInfo = (rate, ct) => {
  const sub = Number(rate) || 0;
  const tax = ct === "exclusive" ? sub * 0.18 : (sub ? sub - sub / 1.18 : 0);
  return { tax, total: ct === "exclusive" ? sub + tax : sub };
};

function JobTotals({ detail }) {
  const { ct, rate, tax, grand, sub } = jobTaxInfo(detail);
  return (
    <div className="border-t border-border pt-3 space-y-1.5" data-testid="job-totals">
      <div className="flex justify-between items-center text-xs">
        <span className="uppercase tracking-[0.2em] text-muted-foreground">Subtotal{ct === "inclusive" ? " (incl. tax)" : " (excl. tax)"}</span>
        <span className="font-mono" data-testid="detail-subtotal">{inr(sub)}</span>
      </div>
      <div className="flex justify-between items-center text-xs">
        <span className="uppercase tracking-[0.2em] text-muted-foreground">{ct === "inclusive" ? `of which GST @ ${rate}%` : `GST @ ${rate}%`}</span>
        <span className="font-mono" data-testid="detail-tax-amount">{ct === "exclusive" ? "+ " : ""}{inr(tax)}</span>
      </div>
      <div className="flex justify-between items-center pt-1">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total{ct === "inclusive" ? " (incl. tax)" : ""}</span>
        <span className="font-display font-black text-2xl text-primary" data-testid="detail-grand-total">{inr(grand)}</span>
      </div>
    </div>
  );
}

function PayDialog({ ctx, onClose }) {
  const [rows, setRows] = useState([{ amount: "", mode: "cash" }]);
  const [busy, setBusy] = useState(false);
  const [paidNow, setPaidNow] = useState(false);
  const [history, setHistory] = useState([]);
  const [livePaid, setLivePaid] = useState(null);
  const [origHist, setOrigHist] = useState([]);
  useEffect(() => {
    setHistory([]);
    setLivePaid(null);
    if (!ctx) return;
    setRows([{ amount: String(ctx.invoice.due || ""), mode: "cash" }]);
    setPaidNow(false);
    if (ctx.invoice?.id) api.get(`/invoices/${ctx.invoice.id}/payments`).then(r => { setHistory(r.data || []); setOrigHist(r.data || []); }).catch(() => {});
  }, [ctx]);
  if (!ctx) return null;
  const { jobcard, invoice } = ctx;
  const due = Number(livePaid?.due ?? invoice.due ?? 0);
  const dirtyRows = history.filter(p => { const o = origHist.find(x => x.id === p.id); return o && (Number(o.amount) !== Number(p.amount) || o.method !== p.method); });
  const newTotal = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const updHist = (id, patch) => setHistory(h => h.map(p => p.id === id ? { ...p, ...patch } : p));
  const saveHist = async (p) => {
    const amt = Number(p.amount);
    if (amt < 0) { toast.error("Amount cannot be negative"); return; }
    try {
      const { data } = await api.patch(`/payments/${p.id}`, { amount: amt, method: p.method });
      setLivePaid(data);
      setOrigHist(h => h.map(o => o.id === p.id ? { ...o, amount: amt, method: p.method } : o));
      toast.success("Payment updated — totals recalculated");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const setRow = (i, patch) => setRows(x => x.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRow = () => {
    const remaining = Math.max(0, Math.round((due - newTotal) * 100) / 100);
    setRows(x => [...x, { amount: remaining ? String(remaining) : "", mode: "cash" }]);
  };
  const delRow = (i) => setRows(x => x.filter((_, j) => j !== i));
  const printReceipt = async () => {
    try {
      const res = await fetch(`${API}/invoices/${invoice.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF generation failed");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const collect = async () => {
    const valid = rows.filter(r => Number(r.amount) > 0 && r.mode !== "credit");
    const creditAmt = rows.filter(r => r.mode === "credit").reduce((a, r) => a + (Number(r.amount) || 0), 0);
    if (!valid.length && !(creditAmt > 0) && !dirtyRows.length) { toast.success("Saved — no payment changes to record"); onClose(); return; }
    if (valid.reduce((a, r) => a + Number(r.amount), 0) > due + 0.01) { toast.error(`Amount exceeds balance due ${inr(due)}`); return; }
    if (!valid.length && !dirtyRows.length && creditAmt > 0) { toast.success("Marked as credit — balance stays due"); onClose(); return; }
    setBusy(true);
    try {
      let last = null;
      for (const p of dirtyRows) {
        const { data } = await api.patch(`/payments/${p.id}`, { amount: Number(p.amount), method: p.method });
        last = data;
      }
      if (dirtyRows.length) setOrigHist(h => h.map(o => { const p = dirtyRows.find(d => d.id === o.id); return p ? { ...o, amount: Number(p.amount), method: p.method } : o; }));
      for (const r of valid) {
        const { data } = await api.post("/payments", { invoice_id: invoice.id, amount: Number(r.amount), method: r.mode });
        last = data;
      }
      if (creditAmt > 0) toast.success(`Credit portion ${inr(creditAmt)} kept as balance due`);
      if (last?.invoice_status === "paid") { setPaidNow(true); toast.success("Payment collected — invoice Paid"); }
      else { toast.success(`Partial payment recorded — balance due ${inr(last?.due ?? 0)}`); onClose(); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ctx} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="pay-dialog" onKeyDownCapture={handleSimpleFormNav}>
        <DialogHeader><DialogTitle className="font-display uppercase">{paidNow ? "Payment Collected" : "Collect Payment"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-border pb-3">
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Job Card</div><div className="font-mono text-xs text-primary" data-testid="pay-job-no">{jobcard.job_number}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Customer</div><div className="text-xs" data-testid="pay-customer">{jobcard.customer_name || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Total</div><div className="font-mono text-xs" data-testid="pay-total">{inr(invoice.total)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Already Paid</div><div className="font-mono text-xs text-green-500" data-testid="pay-paid">{inr(livePaid?.paid ?? invoice.paid ?? 0)}</div></div>
            <div className="col-span-2"><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Balance Due</div><div className="font-display font-black text-2xl text-primary" data-testid="pay-due">{inr(due)}</div></div>
          </div>
          {history.length > 0 && !paidNow && (
            <div className="border border-border rounded-sm p-2" data-testid="pay-history">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Already Collected (editable)</div>
              {history.map((p, i) => (
                <div key={p.id} data-testid={`pay-hist-${i}`} className="grid grid-cols-[70px_1fr_110px_24px] gap-2 items-center text-xs font-mono border-t border-border/50 py-1">
                  <span className="text-muted-foreground">{(p.created_at || "").slice(0, 10)}</span>
                  <Input type="number" min="0" data-testid={`pay-hist-amount-${i}`} value={p.amount} onChange={(e) => updHist(p.id, { amount: e.target.value })} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <Select value={INSTANT_PAY_MODES.some(([v]) => v === p.method) && p.method !== "credit" ? p.method : "cash"} onValueChange={(v) => updHist(p.id, { method: v })}>
                    <SelectTrigger data-testid={`pay-hist-mode-${i}`} className="h-7 rounded-sm bg-secondary border-border text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>{INSTANT_PAY_MODES.filter(([v]) => v !== "credit").map(([v, l]) => <SelectItem key={v} value={v} data-testid={`pay-hist-mode-${i}-${v}`}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" data-testid={`pay-hist-save-${i}`} onClick={() => saveHist(p)} className="text-green-500 justify-self-end" title="Save payment"><CheckCircle className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          )}
          {paidNow ? (
            <div className="space-y-3">
              <div className="text-xs text-green-500 uppercase tracking-wider" data-testid="pay-success">Payment recorded · Invoice Paid</div>
              <div className="flex gap-2">
                <Button data-testid="pay-print-receipt" onClick={printReceipt} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/> Print Receipt</Button>
                <Button data-testid="pay-close-btn" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.15em] text-xs">Close</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Collect (split across modes allowed)</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="pay-add-split" onClick={addRow} className="h-6 rounded-sm text-[10px] uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/> Split</Button>
                </div>
                {rows.map((r, i) => (
                  <div key={i} data-testid={`pay-split-row-${i}`} className="grid grid-cols-[1fr_130px_28px] gap-2 items-center">
                    <Input data-testid={`pay-split-amount-${i}`} type="number" min="0" placeholder="Amount" value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} className="h-8 rounded-sm bg-secondary border-border font-mono text-right"/>
                    <Select value={r.mode} onValueChange={(v) => setRow(i, { mode: v })}>
                      <SelectTrigger data-testid={`pay-split-mode-${i}`} className="h-8 rounded-sm bg-secondary border-border text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>{INSTANT_PAY_MODES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`pay-split-mode-${i}-${v}`}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" data-testid={`pay-split-del-${i}`} onClick={() => delRow(i)} disabled={rows.length === 1} className="text-destructive justify-self-end disabled:opacity-30"><X className="w-3.5 h-3.5"/></button>
                  </div>
                ))}
                <div className="flex justify-between text-xs font-mono border-t border-border pt-2" data-testid="pay-new-total-row">
                  <span className="text-muted-foreground uppercase tracking-wider text-[10px]">Collecting now</span>
                  <span className={newTotal > due ? "text-destructive" : "text-primary"} data-testid="pay-new-total">{inr(newTotal)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="pay-collect-btn" onClick={collect} disabled={busy} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">{busy ? "Saving…" : "Collect Payment"}</Button>
                <Button data-testid="pay-cancel-btn" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.15em] text-xs">Cancel</Button>
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Credit keeps the balance due until collected</div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
function ModifyActionsDialog({ job, onClose, onPay, onCreateInvoice, onReload }) {
  const [state, setState] = useState({ loading: true, challan: null, invoice: null });
  const [busy, setBusy] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);

  useEffect(() => {
    if (!job) return;
    (async () => {
      setState({ loading: true, challan: null, invoice: null });
      setSavedOnce(false);
      try {
        // Save-first flow: PATCH the jobcard with its current (already auto-saved) detail state
        // so the linked invoice fully re-syncs (customer/vehicle/parts/labour/totals/GST).
        await api.patch(`/jobcards/${job.id}`, {
          customer_id: job.customer_id, vehicle_id: job.vehicle_id,
          complaints: job.complaints, mechanic_id: job.mechanic_id || null,
          odometer: job.odometer !== null && job.odometer !== undefined ? Number(job.odometer) : null,
          job_type: job.job_type || job.service_type,
          notes: job.notes || null,
          next_service_date: job.next_service_date || null,
          calc_type: job.calc_type || "inclusive",
          store_id: job.store_id || null,
          rate_lines: job.rate_lines || [], parts: job.parts || [],
        });
        setSavedOnce(true);
        toast.success("Job card updated");
      } catch (e) { toast.error(formatErr(e)); }
      // Always reflect the true challan/invoice state, even if the save failed
      try {
        const [cRes, iRes] = await Promise.all([
          api.get(`/jobcards/${job.id}/challans`),
          api.get(`/jobcards/${job.id}/invoices`),
        ]);
        const challan = (cRes.data || []).find(c => c.status !== "Converted to Invoice") || null;
        const invoice = (iRes.data || []).find(i => i.status !== "revoked") || null;
        setState({ loading: false, challan, invoice });
        onReload?.();
      } catch (e) { toast.error(formatErr(e)); setState({ loading: false, challan: null, invoice: null }); }
    })();
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!job) return null;

  const openPayForInvoice = async (invId) => {
    try {
      const { data } = await api.get(`/invoices`);
      const fresh = (data || []).find(i => i.id === invId);
      if (fresh) onPay(fresh);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const genChallan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobcards/${job.id}/challan`);
      toast.success(`Challan ${data.challan_number} generated`);
      setState(s => ({ ...s, challan: data }));
      onReload?.();
      if (state.invoice) openPayForInvoice(state.invoice.id);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const modChallan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/challans/${state.challan.id}/sync-from-jobcard`);
      toast.success(`Challan ${data.challan_number} updated with latest job card data`);
      setState(s => ({ ...s, challan: data }));
      onReload?.();
      if (state.invoice) openPayForInvoice(state.invoice.id);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const genInvoice = async () => {
    setBusy(true);
    try { await onCreateInvoice(job); } finally { setBusy(false); }
  };
  const modInvoice = async () => {
    // Invoice already synced by the save-first PATCH on dialog open — jump straight to payment.
    openPayForInvoice(state.invoice.id);
  };

  return (
    <Dialog open={!!job} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="modify-dialog" onKeyDownCapture={handleSimpleFormNav}>
        <DialogHeader><DialogTitle className="font-display uppercase">Modify Job Card</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-border pb-3">
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Job Card</div><div className="font-mono text-xs text-primary" data-testid="modify-job-no">{job.job_number}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Customer</div><div className="text-xs" data-testid="modify-customer">{job.customer_name || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Vehicle</div><div className="text-xs font-mono" data-testid="modify-vehicle">{job.vehicle_reg || job.vehicle_label || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Amount</div><div className="font-mono text-xs" data-testid="modify-amount">{inr(job.grand_total || 0)}</div></div>
          </div>
          {state.loading ? (
            <div className="text-xs text-muted-foreground uppercase tracking-wider py-6 text-center" data-testid="modify-saving">Saving job card…</div>
          ) : (
            <>
              {savedOnce && <div className="text-xs text-green-500 uppercase tracking-wider" data-testid="modify-saved">Job card saved · select the next action</div>}
              <div className="grid grid-cols-2 gap-3">
                {state.challan ? (
                  <Button data-testid="modify-mod-challan" onClick={modChallan} disabled={busy} variant="outline" className="h-20 rounded-sm border-primary/40 text-primary uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                    <FileText className="w-5 h-5"/> Modify Challan
                  </Button>
                ) : (
                  <Button data-testid="modify-gen-challan" onClick={genChallan} disabled={busy} variant="outline" className="h-20 rounded-sm border-primary/40 text-primary uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                    <FileText className="w-5 h-5"/> Generate Challan
                  </Button>
                )}
                {state.invoice ? (
                  <Button data-testid="modify-mod-invoice" onClick={modInvoice} disabled={busy} className="h-20 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                    <IndianRupee className="w-5 h-5"/> Modify Invoice
                  </Button>
                ) : (
                  <Button data-testid="modify-gen-invoice" onClick={genInvoice} disabled={busy} className="h-20 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                    <IndianRupee className="w-5 h-5"/> Generate Invoice
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ job, onClose, onInvoice }) {
  const [busy, setBusy] = useState(false);
  const [challan, setChallan] = useState(null);
  useEffect(() => { if (job) setChallan(null); }, [job]);
  if (!job) return null;
  const genChallan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobcards/${job.id}/challan`);
      setChallan(data);
      toast.success(`Challan ${data.challan_number} generated — job card: Challan Generated`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const genInvoice = async () => {
    setBusy(true);
    try { await onInvoice(job); } finally { setBusy(false); }
  };
  const printChallan = async () => {
    try {
      const res = await fetch(`${API}/challans/${challan.id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF generation failed");
      window.open(URL.createObjectURL(await res.blob()), "_blank");
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!job} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm bg-card border-border max-w-md" data-testid="complete-dialog" onKeyDownCapture={handleSimpleFormNav}>
        <DialogHeader><DialogTitle className="font-display uppercase">{challan ? "Challan Generated" : "Complete Job Card"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-border pb-3">
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Job Card</div><div className="font-mono text-xs text-primary" data-testid="complete-job-no">{job.job_number}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Customer</div><div className="text-xs" data-testid="complete-customer">{job.customer_name || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Vehicle</div><div className="text-xs font-mono" data-testid="complete-vehicle">{job.vehicle_reg || job.vehicle_label || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Amount</div><div className="font-mono text-xs" data-testid="complete-amount">{inr(job.grand_total || 0)}</div></div>
          </div>
          {challan ? (
            <div className="space-y-3">
              <div className="text-xs text-green-500 uppercase tracking-wider" data-testid="challan-success">Challan {challan.challan_number} generated · Status: Challan Generated</div>
              <div className="flex gap-2">
                <Button data-testid="challan-print-btn" onClick={printChallan} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/> Print Challan</Button>
                <Button data-testid="challan-close-btn" variant="outline" onClick={onClose} className="rounded-sm uppercase tracking-[0.15em] text-xs">Close</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="complete-gen-challan" onClick={genChallan} disabled={busy} variant="outline" className="h-20 rounded-sm border-primary/40 text-primary uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                <FileText className="w-5 h-5"/> Generate Challan
              </Button>
              <Button data-testid="complete-gen-invoice" onClick={genInvoice} disabled={busy} className="h-20 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs font-bold flex-col gap-1">
                <IndianRupee className="w-5 h-5"/> Generate Invoice
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const emptyVeh = { make: "", model: "", registration: "", year: 2024, vehicle_type: "motorcycle", odometer: 0 };

export default function JobCards() {
  const { user } = useAuth();
  const { minimize, consumeRestore, pending } = useMinimized();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [jobCardTypes, setJobCardTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [stores, setStores] = useState([]);

  // Creation dialog
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyForm);

  // Add customer inline
  const [custOpen, setCustOpen] = useState(false);
  const [newCust, setNewCust] = useState(emptyCust);

  // Add vehicle inline
  const [vehOpen, setVehOpen] = useState(false);
  const [newVeh, setNewVeh] = useState(emptyVeh);

  // Detail dialog
  const [detail, setDetail] = useState(null);
  const [jobChallans, setJobChallans] = useState([]);
  const [jobInvoices, setJobInvoices] = useState([]);
  const [jtCat, setJtCat] = useState("");
  const [jtGroup, setJtGroup] = useState("");
  const [noteText, setNoteText] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({});

  const isStaff = ["owner", "manager", "cashier", "receptionist", "super_admin"].includes(user.role);
  const canEditLines = ["owner", "manager", "cashier", "receptionist", "mechanic", "super_admin"].includes(user.role);

  const defaultJobStore = () => {
    if (user.assigned_store && stores.some(s => s.id === user.assigned_store)) return user.assigned_store;
    const ws = stores.find(s => s.store_type === "Workshop Store");
    return (ws || stores[0] || {}).id || "";
  };

  const load = () => api.get("/jobcards").then(r => setJobs(r.data));
  const loadCustomers = () => api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});

  useEffect(() => {
    load();
    if (isStaff) {
      loadCustomers();
      api.get("/employees/mechanics").then(r => setMechanics(r.data)).catch(() => {});
    }
    api.get("/parts").then(r => setParts(r.data)).catch(() => {});
    api.get("/stores").then(r => setStores(r.data.filter(s => s.status === "Active"))).catch(() => {});
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/job-types").then(r => setJobTypes(r.data)).catch(() => {});
    api.get("/job-card-types").then(r => setJobCardTypes(r.data)).catch(() => {});
  }, [isStaff]);

  useEffect(() => {
    if (f.customer_id) api.get(`/vehicles?customer_id=${f.customer_id}`).then(r => setVehicles(r.data));
    else setVehicles([]);
  }, [f.customer_id]);

  // Restore minimised Job Card dialogs (new or edit) when the user clicks them in the taskbar.
  useEffect(() => {
    const pNew = consumeRestore("jobcard-new");
    if (pNew) { setF(pNew.f); setOpen(true); return; }
    const pEdit = consumeRestore("jobcard-edit");
    if (pEdit) { setEditJob(pEdit.editJob); setEf(pEdit.ef); setERateLines(pEdit.eRateLines || []); setEPartLines(pEdit.ePartLines || []); return; }
    const pPage = consumeRestore("jobcard-page");
    if (pPage) {
      setDateFrom(pPage.dateFrom || todayISO());
      setDateTo(pPage.dateTo || todayISO());
      setJobTypeFilter(pPage.jobTypeFilter || "all");
      setJtCat(pPage.jtCat || ""); setJtGroup(pPage.jtGroup || "");
      setNoteText(pPage.noteText || "");
      if (pPage.detailId) openDetail(pPage.detailId).catch(() => {});
    }
  }, [pending]); // eslint-disable-line

  const refOfNewJob = () => {
    const c = customers.find(x => x.id === f.customer_id);
    return c?.name ? `For: ${c.name}` : "New";
  };
  const isDirtyNew = () => !!(f.customer_id || f.vehicle_id || f.complaints || f.job_type || f.mechanic_id || f.next_service_date
    || f.inst_name || f.inst_mobile || f.inst_vehicle || f.inst_rate || f.inst_remarks || (f.inst_services || []).length);
  const minimiseNew = () => {
    minimize({ kind: "jobcard-new", label: "New Job Card", ref: refOfNewJob(), state: { f }, path: "/jobcards", dirty: isDirtyNew() });
    setOpen(false); setF(emptyForm);
    toast.success("Minimised to taskbar");
  };
  const minimiseEdit = () => {
    minimize({ kind: "jobcard-edit", label: "Edit Job Card", ref: editJob.job_number, state: { editJob, ef, eRateLines, ePartLines }, path: "/jobcards", dirty: true });
    setEditJob(null);
    toast.success("Minimised to taskbar");
  };
  const minimisePage = () => {
    minimize({
      kind: "jobcard-page",
      label: "Job Cards",
      ref: detail ? detail.job_number : "List",
      state: {
        dateFrom, dateTo, jobTypeFilter,
        detailId: detail?.id || null,
        jtCat, jtGroup, noteText,
      },
      path: "/jobcards",
      dirty: !!noteText.trim(),
    });
    setDetail(null); setPhotoUrls({}); setJobChallans([]); setJobInvoices([]);
    toast.success("Job Cards page minimised to taskbar");
  };

  const saveNewCustomer = async () => {
    if (!newCust.name || !newCust.phone) { toast.error("Name and phone are required"); return; }
    try {
      const { data } = await api.post("/customers", {
        name: newCust.name, phone: newCust.phone,
        address: newCust.address || null, email: null,
      });
      await loadCustomers();
      setF(x => ({ ...x, customer_id: data.id, vehicle_id: "" }));
      setNewCust(emptyCust); setCustOpen(false);
      toast.success("Customer added & selected");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveNewVehicle = async () => {
    if (!f.customer_id) { toast.error("Select a customer first"); return; }
    if (!newVeh.make || !newVeh.model || !newVeh.registration) { toast.error("Make, Model & Registration required"); return; }
    try {
      const { data } = await api.post("/vehicles", {
        ...newVeh, customer_id: f.customer_id,
        year: Number(newVeh.year), odometer: Number(newVeh.odometer),
      });
      const list = await api.get(`/vehicles?customer_id=${f.customer_id}`);
      setVehicles(list.data);
      setF(x => ({ ...x, vehicle_id: data.id }));
      setNewVeh(emptyVeh); setVehOpen(false);
      toast.success("Vehicle added & selected");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submit = async () => {
    if (!f.job_type) { toast.error("Job Type is required"); return; }
    try {
      await api.post("/jobcards", {
        customer_id: f.customer_id, vehicle_id: f.vehicle_id,
        complaints: f.complaints, mechanic_id: f.mechanic_id || null,
        store_id: f.store_id || null, next_service_date: f.next_service_date || null,
        job_type: f.job_type, job_date: f.job_date || null,
        labour_charges: 0, parts: [], rate_lines: [],
      });
      toast.success("Job card created");
      setF(emptyForm); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const [payCtx, setPayCtx] = useState(null);
  const [rowPayCtx, setRowPayCtx] = useState(null);

  const [completeJob, setCompleteJob] = useState(null);
  const [modifyJob, setModifyJob] = useState(null);
  const openPayFromRow = async (j) => {
    if (!j.invoice_id) { toast.error("No invoice linked to this job card"); return; }
    try {
      const { data } = await api.get(`/invoices/${j.invoice_id}`);
      setRowPayCtx({ jobcard: j, invoice: data });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const instRateSum = (svcs) => svcs.reduce((a, s) => a + (Number(s.labour_rate) || 0), 0);
  const addInstService = (jtId) => {
    const jt = jobTypes.find(x => x.id === jtId);
    if (!jt) return;
    setF(x => {
      if (x.inst_services.some(s => s.job_type_id === jtId)) return x;
      const svcs = [...x.inst_services, { job_type_id: jt.id, name: jt.name, labour_rate: jt.labour_rate }];
      return { ...x, inst_services: svcs, inst_rate: String(instRateSum(svcs)) };
    });
  };
  const delInstService = (jtId) => setF(x => {
    const svcs = x.inst_services.filter(s => s.job_type_id !== jtId);
    return { ...x, inst_services: svcs, inst_rate: svcs.length ? String(instRateSum(svcs)) : x.inst_rate };
  });

  const submitInstant = async () => {
    if (!f.inst_name.trim()) { toast.error("Customer name is required"); return; }
    if (!(Number(f.inst_rate) > 0)) { toast.error("Job Rate must be greater than zero"); return; }
    try {
      const { data } = await api.post("/jobcards/instant", {
        customer_name: f.inst_name.trim(), mobile: f.inst_mobile.trim() || null, vehicle_number: f.inst_vehicle.trim() || null,
        mechanic_id: f.mechanic_id || null, services: f.inst_services,
        job_rate: Number(f.inst_rate), calc_type: f.inst_calc || "inclusive", remarks: f.inst_remarks || null,
        job_date: f.job_date || null,
      });
      toast.success(`Job card ${data.jobcard.job_number} + invoice ${data.invoice.invoice_number} created`);
      setF(emptyForm); setOpen(false); load();
      setPayCtx({ jobcard: data.jobcard, invoice: data.invoice });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const collectFromList = async (j) => {
    try {
      const { data } = await api.get(`/jobcards/${j.id}/invoices`);
      const inv = (data || []).find(x => x.status !== "paid") || (data || [])[0];
      if (!inv) { toast.error("No invoice found for this job card"); return; }
      setPayCtx({ jobcard: j, invoice: inv });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setStatus = async (jid, status) => {
    try {
      await api.patch(`/jobcards/${jid}/status`, { status });
      toast.success(`Status: ${status.replace("_"," ")}`);
      load();
      if (detail && detail.id === jid) {
        const { data } = await api.get(`/jobcards/${jid}`);
        setDetail(data);
      }
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openDetail = async (jid) => {
    const { data } = await api.get(`/jobcards/${jid}`);
    setDetail(data);
    setPhotoUrls({});
    loadJobLinks(jid);
    // Prefetch photo blobs for display (auth-safe)
    for (const p of (data.photos || []).filter(x => !x.is_deleted)) {
      loadPhotoBlob(p);
    }
  };

  const loadJobLinks = async (jid) => {
    try {
      const [c, i] = await Promise.all([api.get(`/jobcards/${jid}/challans`), api.get(`/jobcards/${jid}/invoices`)]);
      setJobChallans(c.data); setJobInvoices(i.data);
    } catch { setJobChallans([]); setJobInvoices([]); }
  };

  const createChallan = async () => {
    if (!detail) return;
    if (!confirm(`Create challan from job ${detail.job_number}?`)) return;
    try {
      const { data } = await api.post(`/jobcards/${detail.id}/challan`);
      toast.success(`Challan ${data.challan_number} created`);
      loadJobLinks(detail.id); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const fetchDocPdf = async (kind, id, num, download) => {
    try {
      const res = await fetch(`${API}/${kind}s/${id}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = `${num}.pdf`; a.click();
      } else window.open(url, "_blank");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const deleteChallan = async (c) => {
    if (!confirm(`Delete challan ${c.challan_number}?`)) return;
    try { await api.delete(`/challans/${c.id}`); toast.success("Challan deleted"); loadJobLinks(detail.id); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const convertChallan = async (c) => {
    if (!confirm(`Convert challan ${c.challan_number} to an invoice?`)) return;
    try {
      const { data } = await api.post(`/challans/${c.id}/convert`);
      toast.success(`Invoice ${data.invoice_number} created`);
      loadJobLinks(detail.id);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteInvoice = async (i) => {
    if (!confirm(`Delete invoice ${i.invoice_number}?`)) return;
    try { await api.delete(`/invoices/${i.id}`); toast.success("Invoice deleted"); loadJobLinks(detail.id); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const revokeInvoice = async (i) => {
    if (!confirm(`Revoke invoice ${i.invoice_number}? Linked challan will revert to Issued. This cannot be undone.`)) return;
    try { await api.post(`/invoices/${i.id}/revoke`); toast.success("Invoice revoked"); loadJobLinks(detail.id); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  const [editJob, setEditJob] = useState(null);
  const [ef, setEf] = useState({ customer_id: "", vehicle_id: "", complaints: "", mechanic_id: "", odometer: "", job_type: "", notes: "", next_service_date: "", store_id: "", job_date: "" });
  const [eRateLines, setERateLines] = useState([]);
  const [ePartLines, setEPartLines] = useState([]);
  const [eSelJT, setESelJT] = useState("");
  const [eSelItem, setESelItem] = useState("");
  const [eSelQty, setESelQty] = useState(1);
  const [eSelPrice, setESelPrice] = useState("");

  const canEditJob = ["owner", "manager", "receptionist", "super_admin"].includes(user.role);
  const canDeleteJob = ["owner", "manager", "super_admin"].includes(user.role);

  const editTotals = useMemo(() => {
    const parts = ePartLines.reduce((a, p) => a + (Number(p.qty) || 0) * (Number(p.price) || 0), 0);
    const labour = eRateLines.reduce((a, l) => a + (Number(l.labour_rate) || 0) * (Number(l.qty) || 1), 0);
    const subtotal = parts + labour;
    const rate = editJob?.tax_rate ?? 18;
    const inclusive = (ef.calc_type || "inclusive") === "inclusive";
    const tax = inclusive ? (rate ? subtotal - subtotal / (1 + rate / 100) : 0) : subtotal * rate / 100;
    const grand = inclusive ? subtotal : subtotal + tax;
    return { parts, labour, tax, grand };
  }, [ePartLines, eRateLines, ef.calc_type, editJob]);

  useEffect(() => {
    if (editJob && ef.customer_id) api.get(`/vehicles?customer_id=${ef.customer_id}`).then(r => setVehicles(r.data));
  }, [ef.customer_id, editJob]);

  const filteredJobs = jobs.filter(j => {
    const day = j.job_date || (j.created_at || "").slice(0, 10);
    return (!dateFrom || day >= dateFrom) && (!dateTo || day <= dateTo)
      && (jobTypeFilter === "all" || (j.job_type || j.service_type || "") === jobTypeFilter);
  });
  const pg = usePagination(filteredJobs);
  const jcTotals = useMemo(() => filteredJobs.reduce((a, j) => {
    const parts = j.parts_total || 0, labour = j.labour_charges || 0;
    return {
      parts: a.parts + parts, labour: a.labour + labour, amount: a.amount + parts + labour,
      cash: a.cash + (j.paid_cash || 0),
      phonepay: a.phonepay + (j.paid_phonepay || 0),
      paytm: a.paytm + (j.paid_paytm || 0),
      paid: a.paid + (j.amount_paid || 0),
      balance: a.balance + (j.balance_due || 0),
    };
  }, { parts: 0, labour: 0, amount: 0, cash: 0, phonepay: 0, paytm: 0, paid: 0, balance: 0 }), [filteredJobs]);
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/organisation").then(r => { const d = r.data; setOrg(Array.isArray(d) ? (d[0] || null) : d); }).catch(() => {}); }, []);

  const openEdit = (j) => {
    setEditJob(j);
    setEf({
      customer_id: j.customer_id || "", vehicle_id: j.vehicle_id || "",
      complaints: j.complaints || "", mechanic_id: j.mechanic_id || "",
      odometer: j.odometer ?? "", job_type: j.job_type || j.service_type || "", notes: j.notes || "",
      next_service_date: j.next_service_date || "", calc_type: j.calc_type || "inclusive",
      store_id: j.store_id || "", job_date: j.job_date || (j.created_at || "").slice(0, 10),
    });
    setERateLines(j.rate_lines || []);
    setEPartLines(j.parts || []);
  };

  const saveEdit = async () => {
    if (!ef.job_type) { toast.error("Job Type is required"); return; }
    try {
      const { data } = await api.patch(`/jobcards/${editJob.id}`, {
        customer_id: ef.customer_id, vehicle_id: ef.vehicle_id,
        complaints: ef.complaints, mechanic_id: ef.mechanic_id || null,
        odometer: ef.odometer !== "" ? Number(ef.odometer) : null,
        job_type: ef.job_type, notes: ef.notes || null,
        next_service_date: ef.next_service_date || null,
        calc_type: ef.calc_type || "inclusive",
        job_date: ef.job_date || null,
        store_id: ef.store_id || null,
        rate_lines: eRateLines, parts: ePartLines,
      });
      toast.success("Job card updated");
      const updated = { ...editJob, ...data };
      setEditJob(null); load();
      setCompleteJob(updated);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const delJob = async (j) => {
    if (!confirm(`Delete job card ${j.job_number}? Linked booking/estimate will be unlinked. This cannot be undone.`)) return;
    try { await api.delete(`/jobcards/${j.id}`); toast.success("Job card deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const eAddRate = () => {
    const jt = jobTypes.find(x => x.id === eSelJT);
    if (!jt) return;
    setERateLines(x => [...x, { job_type_id: jt.id, name: jt.name, labour_rate: jt.labour_rate }]);
    setESelJT("");
  };

  const eAddPart = () => {
    const it = items.find(i => i.id === eSelItem);
    if (!it) return;
    setEPartLines(x => [...x, { item_id: it.id, name: it.item_name, qty: Number(eSelQty) || 1, price: Number(eSelPrice || it.mrp || 0) }]);
    setESelItem(""); setESelQty(1); setESelPrice("");
  };

  const exportPdf = async () => {
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (jobTypeFilter && jobTypeFilter !== "all") p.set("job_type", jobTypeFilter);
      const res = await fetch(`${API}/jobcards/export.pdf?${p.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "jobcards_export.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };


  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  useEffect(() => {
    const oid = searchParams.get("open");
    if (oid) {
      openDetail(oid).catch(() => toast.error("Job card not found"));
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadPhotoBlob = async (p) => {
    try {
      const res = await api.get(`/files/${p.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPhotoUrls(prev => ({ ...prev, [p.id]: url }));
    } catch {}
  };

  const addNote = async () => {
    if (!noteText.trim() || !detail) return;
    try {
      const { data } = await api.post(`/jobcards/${detail.id}/notes`, { text: noteText });
      setDetail(data); setNoteText("");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const delNote = async (nid) => {
    try {
      const { data } = await api.delete(`/jobcards/${detail.id}/notes/${nid}`);
      setDetail(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadPhoto = async (files) => {
    if (!files || files.length === 0 || !detail) return;
    setPhotoUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/jobcards/${detail.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDetail(data);
        const last = (data.photos || []).filter(p => !p.is_deleted).slice(-1)[0];
        if (last) loadPhotoBlob(last);
      }
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPhotoUploading(false); }
  };
  const delPhoto = async (pid) => {
    try {
      const { data } = await api.delete(`/jobcards/${detail.id}/photos/${pid}`);
      setDetail(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const printJobSheet = () => window.print();

  const setRlLocal = (i, patch) => setDetail(d => d ? ({ ...d, rate_lines: d.rate_lines.map((r, j) => j === i ? { ...r, ...patch } : r) }) : d);
  const addRateLineFromMaster = async (jt) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/jobcards/${detail.id}/rate-lines`, { job_type_id: jt.id });
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const addManualRateLine = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/jobcards/${detail.id}/rate-lines`, { name: "New Service", unit: "Job", labour_rate: 0, qty: 1 });
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const updateRateLine = async (idx, patch) => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/jobcards/${detail.id}/rate-lines/${idx}`, patch);
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeRateLine = async (idx) => {
    try {
      const { data } = await api.delete(`/jobcards/${detail.id}/rate-lines/${idx}`);
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const setPartLocal = (i, patch) => setDetail(d => d ? ({ ...d, parts: d.parts.map((r, j) => j === i ? { ...r, ...patch } : r) }) : d);
  const addPartFromMaster = async (it) => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/jobcards/${detail.id}/part-lines`, { item_id: it.id, qty: 1 });
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const updatePartLine = async (idx, qty) => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/jobcards/${detail.id}/part-lines/${idx}`, { qty });
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const updatePartLineRate = async (idx, price) => {
    if (!detail) return;
    const p = detail.parts[idx];
    if (!p || price < 0) return;
    try {
      const { data } = await api.patch(`/jobcards/${detail.id}/part-lines/${idx}`, { qty: p.qty, price });
      setDetail(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removePartLine = async (idx) => {
    try {
      const { data } = await api.delete(`/jobcards/${detail.id}/part-lines/${idx}`);
      setDetail(data); load();
      const p = await api.get("/parts"); setParts(p.data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const createInvoice = async (jid, job) => {
    let redeem = 0;
    try {
      const { data: cust } = await api.get(`/customers/${job.customer_id}/loyalty`);
      if (cust.points > 0) {
        const ans = prompt(`Customer has ${cust.points} loyalty points. Redeem how many? (0 to skip)`, "0");
        redeem = Math.max(0, Math.min(cust.points, Number(ans) || 0));
      }
    } catch {}
    try {
      const { data } = await api.post("/invoices", { job_card_id: jid, gst_rate: 18, discount: 0, loyalty_redeem: redeem });
      toast.success(`Invoice ${data.invoice_number} created${redeem ? ` · ${redeem} pts redeemed` : ""}`);
      return data;
    } catch (e) { toast.error(formatErr(e)); return null; }
  };

  const completeInvoice = async (job) => {
    const inv = await createInvoice(job.id, job);
    if (!inv) return;
    setCompleteJob(null); load();
    if (detail && detail.id === job.id) openDetail(job.id);
    setPayCtx({ invoice: inv });
  };

  const editable = detail && (detail.status === "in_progress" || detail.status === "completed");

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Service Bay</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Job Cards</h1>
        </div>
        <div className="flex items-center gap-2">
        {isStaff && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setF(x => ({ ...x, store_id: x.store_id || defaultJobStore(), job_date: x.job_date || new Date().toISOString().slice(0, 10) })); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-jobcard-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Job
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
              <DialogHeader>
                <DialogTitle className="font-display uppercase flex items-center justify-between gap-4">
                  <span>New Job Card</span>
                  <Button data-testid="minimise-jobcard-btn" type="button" size="sm" variant="outline" onClick={minimiseNew} className="rounded-sm uppercase tracking-[0.15em] text-xs mr-6" title="Minimise to taskbar"><Minimize2 className="w-3 h-3 mr-1"/> Minimise</Button>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Job Type *</Label>
                    <Select value={f.job_type} onValueChange={(v) => setF({ ...f, job_type: v })}>
                      <SelectTrigger data-testid="job-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select job type"/></SelectTrigger>
                      <SelectContent>{jobCardTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date *</Label>
                    <Input data-testid="job-date" type="date" max={new Date().toISOString().slice(0, 10)} value={f.job_date} onChange={(e) => setF({ ...f, job_date: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                </div>
                {f.job_type === "Instant Service" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer Name *</Label>
                        <Input data-testid="inst-cust-name" value={f.inst_name} onChange={(e) => setF(x => ({ ...x, inst_name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile Number</Label>
                        <Input data-testid="inst-mobile" value={f.inst_mobile} onChange={(e) => setF(x => ({ ...x, inst_mobile: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle Number</Label>
                      <Input data-testid="inst-vehicle" value={f.inst_vehicle} onChange={(e) => setF(x => ({ ...x, inst_vehicle: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. MH01AB1234"/>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Assign Mechanic</Label>
                      <Select value={f.mechanic_id} onValueChange={(v) => setF({ ...f, mechanic_id: v })}>
                        <SelectTrigger data-testid="job-mechanic" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                        <SelectContent>{mechanics.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Calculation Type</Label>
                      <Select value={f.inst_calc} onValueChange={(v) => setF({ ...f, inst_calc: v })}>
                        <SelectTrigger data-testid="inst-calc-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inclusive" data-testid="inst-calc-inclusive">Inclusive Tax</SelectItem>
                          <SelectItem value="exclusive" data-testid="inst-calc-exclusive">Exclusive Tax</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Service Selection</Label>
                      <Select value="" onValueChange={addInstService}>
                        <SelectTrigger data-testid="inst-service-select" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Add a service"/></SelectTrigger>
                        <SelectContent>{jobTypes.filter(jt => !f.inst_services.some(s => s.job_type_id === jt.id)).map(jt => <SelectItem key={jt.id} value={jt.id} data-testid={`inst-service-opt-${jt.id}`}>{jt.name} · ₹{jt.labour_rate}</SelectItem>)}</SelectContent>
                      </Select>
                      {f.inst_services.map((s) => (
                        <div key={s.job_type_id} data-testid={`inst-service-line-${s.job_type_id}`} className="flex items-center justify-between text-xs border-t border-border py-1.5 mt-1">
                          <span>{s.name}</span>
                          <span className="flex items-center gap-2 font-mono">{inr(s.labour_rate)}
                            <button type="button" data-testid={`inst-service-del-${s.job_type_id}`} onClick={() => delInstService(s.job_type_id)} className="text-destructive"><X className="w-3 h-3"/></button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Job Rate *</Label>
                      <Input data-testid="inst-rate" type="number" min="0" value={f.inst_rate} onChange={(e) => setF(x => ({ ...x, inst_rate: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                    </div>
                    <div className="text-xs text-muted-foreground border-t border-border pt-2 flex items-center justify-between gap-2" data-testid="inst-tax-summary">
                      <span className="uppercase tracking-wider">{f.inst_calc === "exclusive" ? "GST @ 18%" : "of which GST @ 18%"}</span>
                      <span className="font-mono" data-testid="inst-tax-amount">{f.inst_calc === "exclusive" ? "+ " : ""}{inr(instTaxInfo(f.inst_rate, f.inst_calc).tax)}</span>
                      <span className="uppercase tracking-wider">{f.inst_calc === "exclusive" ? "Total" : "Total (incl. tax)"}</span>
                      <span className="font-mono text-primary" data-testid="inst-total">{inr(instTaxInfo(f.inst_rate, f.inst_calc).total)}</span>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Remarks</Label>
                      <Textarea data-testid="inst-remarks" value={f.inst_remarks} onChange={(e) => setF(x => ({ ...x, inst_remarks: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v, vehicle_id: "" })}>
                          <SelectTrigger data-testid="job-customer" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder="Select customer"/></SelectTrigger>
                          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button
                          type="button"
                          data-testid="add-customer-inline-btn"
                          onClick={() => setCustOpen(true)}
                          variant="outline"
                          className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0"
                          title="Add new customer"
                        >
                          <UserPlus className="w-4 h-4"/>
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v })}>
                          <SelectTrigger data-testid="job-vehicle" className="rounded-sm bg-secondary border-border flex-1"><SelectValue placeholder={f.customer_id ? "Select vehicle" : "Pick customer first"}/></SelectTrigger>
                          <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration} · {v.make} {v.model}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button
                          type="button"
                          data-testid="add-vehicle-inline-btn"
                          onClick={() => setVehOpen(true)}
                          disabled={!f.customer_id}
                          variant="outline"
                          className="rounded-sm border-primary/40 text-primary hover:bg-primary/10 shrink-0"
                        >
                          <Plus className="w-4 h-4"/>
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Complaints / Issue Description</Label>
                      <Textarea data-testid="job-complaints" value={f.complaints} onChange={(e) => setF(x => ({ ...x, complaints: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={4} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Assign Mechanic</Label>
                      <Select value={f.mechanic_id} onValueChange={(v) => setF({ ...f, mechanic_id: v })}>
                        <SelectTrigger data-testid="job-mechanic" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                        <SelectContent>{mechanics.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
                      <Select value={f.store_id} onValueChange={(v) => setF({ ...f, store_id: v })}>
                        <SelectTrigger data-testid="job-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select store"/></SelectTrigger>
                        <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.store_type}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Next Service Date</Label>
                      <Input data-testid="job-next-service-date" type="date" value={f.next_service_date} onChange={(e) => setF(x => ({ ...x, next_service_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                    </div>
                    <div className="text-xs text-muted-foreground border-t border-border pt-3">
                      Job rate & parts used are added after the job moves to <span className="text-primary uppercase tracking-wider">In Progress</span>.
                    </div>
                  </>
                )}
                <div className="flex gap-2">
                  <Button data-testid="save-jobcard-btn" onClick={f.job_type === "Instant Service" ? submitInstant : submit} disabled={f.job_type === "Instant Service" ? (!f.inst_name.trim() || !(Number(f.inst_rate) > 0)) : (!f.customer_id || !f.vehicle_id || !f.complaints || !f.job_type)} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                    {f.job_type === "Instant Service" ? "Save & Create Invoice" : "Create Job Card"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        </div>

        <CompleteDialog job={completeJob} onClose={() => { const jid = completeJob?.id; setCompleteJob(null); load(); if (detail && jid === detail.id) openDetail(jid); }} onInvoice={completeInvoice}/>
        <ModifyActionsDialog
          job={modifyJob}
          onClose={() => { const jid = modifyJob?.id; setModifyJob(null); load(); if (detail && jid === detail.id) openDetail(jid); }}
          onPay={(inv) => { setModifyJob(null); setPayCtx({ invoice: inv }); }}
          onCreateInvoice={async (j) => {
            const inv = await createInvoice(j.id, j);
            if (!inv) return;
            setModifyJob(null); load();
            if (detail && detail.id === j.id) openDetail(j.id);
            setPayCtx({ invoice: inv });
          }}
          onReload={() => { load(); if (detail && detail.id === modifyJob?.id) loadJobLinks(detail.id); }}
        />
        <InstantPayDialog ctx={payCtx} onClose={() => { setPayCtx(null); load(); }}/>
        <PayDialog ctx={rowPayCtx} onClose={() => { setRowPayCtx(null); load(); }}/>

        {/* Add New Customer */}
        <Dialog open={custOpen} onOpenChange={setCustOpen}>
          <DialogContent className="rounded-sm bg-card border-border max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary"/> New Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label>
                <Input data-testid="inline-cust-name" value={newCust.name} onChange={(e) => setNewCust(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone Number</Label>
                <Input data-testid="inline-cust-phone" value={newCust.phone} onChange={(e) => setNewCust(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                <Textarea data-testid="inline-cust-address" value={newCust.address} onChange={(e) => setNewCust(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
              </div>
              <Button data-testid="save-inline-customer-btn" onClick={saveNewCustomer} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save & Select</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add New Vehicle */}
        <Dialog open={vehOpen} onOpenChange={setVehOpen}>
          <DialogContent className="rounded-sm bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-display uppercase flex items-center gap-2"><BikeIcon className="w-5 h-5 text-primary"/> New Vehicle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Owner: <span className="text-foreground">{customers.find(c => c.id === f.customer_id)?.name || "—"}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[["make", "Make"], ["model", "Model"], ["registration", "Registration"], ["year", "Year"], ["odometer", "Odometer"]].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{l}</Label>
                    <Input data-testid={`inline-veh-${k}`} value={newVeh[k]} onChange={(e) => setNewVeh(x => ({ ...x, [k]: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Type</Label>
                  <Select value={newVeh.vehicle_type} onValueChange={(v) => setNewVeh(x => ({ ...x, vehicle_type: v }))}>
                    <SelectTrigger className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      <SelectItem value="scooter">Scooter</SelectItem>
                      <SelectItem value="ev">Electric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="save-inline-vehicle-btn" onClick={saveNewVehicle} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save & Select</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3 print:hidden">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From Date</Label>
          <Input data-testid="jc-filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To Date</Label>
          <Input data-testid="jc-filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/>
        </div>
        <Button data-testid="jc-filter-today" size="sm" variant="outline" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="rounded-sm text-xs uppercase tracking-wider">Today</Button>
        <Button data-testid="jc-filter-all" size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-sm text-xs uppercase tracking-wider">All Dates</Button>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Job Type</Label>
          <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
            <SelectTrigger data-testid="jc-filter-jobtype" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Job Types</SelectItem>
              {jobCardTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono mr-2">{filteredJobs.length} of {jobs.length} jobs</span>
          <Button data-testid="export-pdf-jobcards" size="sm" variant="outline" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
          <Button data-testid="print-jobcards" size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto print:hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th rowSpan={2} className="text-left p-3 align-bottom">Job Card #</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Date</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Customer</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Mobile</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Vehicle</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Job Type</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Mechanic</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Status</th>
              <th colSpan={3} className="text-center p-2 border-l border-b border-border/60" data-testid="jc-amount-group-header">Amount</th>
              <th colSpan={4} className="text-center p-2 border-l border-b border-border/60" data-testid="jc-paid-group-header">Paid</th>
              <th rowSpan={2} className="text-right p-3 align-bottom border-l border-border/60">Balance</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Payment</th>
              <th rowSpan={2} className="text-right p-3 align-bottom">Actions</th>
            </tr>
            <tr>
              <th className="text-right px-3 pb-2 border-l border-border/60">Parts</th>
              <th className="text-right px-3 pb-2">Labour</th>
              <th className="text-right px-3 pb-2">Total</th>
              <th className="text-right px-3 pb-2 border-l border-border/60">Cash</th>
              <th className="text-right px-3 pb-2">PhonePay</th>
              <th className="text-right px-3 pb-2">Paytm</th>
              <th className="text-right px-3 pb-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length === 0 && <tr><td colSpan={18} className="p-6 text-center text-muted-foreground">No job cards for the selected date range</td></tr>}
            {pg.pageItems.map(j => (
              <tr key={j.id} className="border-t border-border hover:bg-secondary/50 align-top cursor-pointer" onClick={() => openDetail(j.id)}>
                <td className="p-3 font-mono text-xs text-primary uppercase" data-testid={`row-${j.id}`}>{j.job_number}</td>
                <td className="p-3 text-muted-foreground text-xs font-mono">{j.job_date || (j.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-xs">{j.customer_name || "-"}</td>
                <td className="p-3 text-xs font-mono" data-testid={`jc-mobile-${j.id}`}>{j.customer_phone || "-"}</td>
                <td className="p-3 text-xs font-mono" data-testid={`jc-vehicle-${j.id}`}>{j.vehicle_reg || j.vehicle_label || "-"}</td>
                <td className="p-3 text-xs" data-testid={`jc-jobtype-${j.id}`}>{j.job_type || j.service_type || "-"}</td>
                <td className="p-3 text-xs">{j.mechanic_name || "-"}</td>
                <td className="p-3"><span data-testid={`jc-status-${j.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm ${displayStatusColor[j.display_status] || "text-muted-foreground bg-secondary"}`}>{j.display_status || j.status}</span></td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`jc-parts-${j.id}`}>{inr(j.parts_total || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`jc-labour-${j.id}`}>{inr(j.labour_charges || 0)}</td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`jc-amount-${j.id}`}>{inr((j.parts_total || 0) + (j.labour_charges || 0))}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`jc-cash-${j.id}`}>{j.invoice_id ? inr(j.paid_cash || 0) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`jc-phonepay-${j.id}`}>{j.invoice_id ? inr(j.paid_phonepay || 0) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`jc-paytm-${j.id}`}>{j.invoice_id ? inr(j.paid_paytm || 0) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs font-semibold" data-testid={`jc-paid-${j.id}`}>{j.invoice_id ? inr(j.amount_paid || 0) : "—"}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`jc-balance-${j.id}`}>{j.invoice_id ? inr(j.balance_due || 0) : "—"}</td>
                <td className="p-3">{j.payment_status && j.payment_status !== "unbilled" ? (
                  <span data-testid={`jc-pay-${j.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${PAY_STATUS[j.payment_status]?.cls || ""}`}>{PAY_STATUS[j.payment_status]?.label || "—"}</span>
                ) : (<span className="text-muted-foreground text-xs" data-testid={`jc-pay-${j.id}`}>—</span>)}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {["unpaid","partial"].includes(j.payment_status) && canEditLines && (
                    <Button data-testid={`pay-${j.id}`} size="sm" variant="ghost" onClick={() => openPayFromRow(j)} className="h-7 text-xs text-orange-400">
                      <IndianRupee className="w-3 h-3 mr-1"/> Pay
                    </Button>
                  )}{j.status === "open" && canEditLines && (
                    <Button data-testid={`start-${j.id}`} size="sm" variant="ghost" onClick={() => setStatus(j.id, "in_progress")} className="h-7 text-xs">
                      <Play className="w-3 h-3 mr-1"/> Start
                    </Button>
                  )}
                  {j.status === "in_progress" && canEditLines && (
                    <Button data-testid={`complete-${j.id}`} size="sm" variant="ghost" onClick={() => setCompleteJob(j)} className="h-7 text-xs text-green-500">
                      <CheckCircle className="w-3 h-3 mr-1"/> Done
                    </Button>
                  )}
                  {j.status === "pending_payment" && canEditLines && (
                    <Button data-testid={`collect-${j.id}`} size="sm" variant="ghost" onClick={() => collectFromList(j)} className="h-7 text-xs text-orange-400">
                      <Banknote className="w-3 h-3 mr-1"/> Collect
                    </Button>
                  )}
                  {isStaff && j.status === "completed" && (
                    <Button data-testid={`bill-${j.id}`} size="sm" variant="ghost" onClick={() => createInvoice(j.id, j)} className="h-7 text-xs text-primary">
                      <FileText className="w-3 h-3 mr-1"/> Bill
                    </Button>
                  )}
                  {isStaff && j.status === "challan_generated" && (
                    <Button data-testid={`invoice-${j.id}`} size="sm" variant="ghost" onClick={() => setCompleteJob(j)} className="h-7 text-xs text-primary">
                      <FileText className="w-3 h-3 mr-1"/> Invoice
                    </Button>
                  )}
                  {canEditJob && j.status === "completed" && (
                    <Button data-testid={`edit-jobcard-${j.id}`} size="sm" variant="ghost" onClick={() => openEdit(j)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  )}
                  {canDeleteJob && (
                    <Button data-testid={`del-jobcard-${j.id}`} size="sm" variant="ghost" onClick={() => delJob(j)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  )}
                </td>
              </tr>
            ))}
            {filteredJobs.length > 0 && (
              <tr data-testid="jc-total-row" className="border-t-2 border-primary/40 bg-secondary/60 font-bold align-top">
                <td className="p-3 text-[10px] uppercase tracking-[0.2em] text-primary" colSpan={8}>Totals · {filteredJobs.length} jobs</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="jc-total-parts">{inr(jcTotals.parts)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="jc-total-labour">{inr(jcTotals.labour)}</td>
                <td className="p-3 text-right font-mono" data-testid="jc-total-amount">{inr(jcTotals.amount)}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="jc-total-cash">{inr(jcTotals.cash)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="jc-total-phonepay">{inr(jcTotals.phonepay)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="jc-total-paytm">{inr(jcTotals.paytm)}</td>
                <td className="p-3 text-right font-mono" data-testid="jc-total-paid">{inr(jcTotals.paid)}</td>
                <td className="p-3 text-right font-mono border-l border-border/60" data-testid="jc-total-balance">{inr(jcTotals.balance)}</td>
                <td colSpan={2}/>
              </tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      {/* Edit Job Card dialog */}
      <Dialog open={!!editJob} onOpenChange={(v) => { if (!v) setEditJob(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center justify-between gap-4">
              <span>Edit Job Card <span className="font-mono text-primary">{editJob?.job_number}</span></span>
              <Button data-testid="minimise-edit-jobcard-btn" type="button" size="sm" variant="outline" onClick={minimiseEdit} className="rounded-sm uppercase tracking-[0.15em] text-xs mr-6" title="Minimise to taskbar"><Minimize2 className="w-3 h-3 mr-1"/> Minimise</Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Job Type *</Label>
                <Select value={ef.job_type} onValueChange={(v) => setEf({ ...ef, job_type: v })}>
                  <SelectTrigger data-testid="edit-job-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select job type"/></SelectTrigger>
                  <SelectContent>{jobCardTypes.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date *</Label>
                <Input data-testid="edit-job-date" type="date" max={new Date().toISOString().slice(0, 10)} value={ef.job_date} onChange={(e) => setEf({ ...ef, job_date: e.target.value })} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Calculation Type</Label>
              <Select value={ef.calc_type || "inclusive"} onValueChange={(v) => setEf({ ...ef, calc_type: v })}>
                <SelectTrigger data-testid="edit-job-calc-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inclusive" data-testid="edit-job-calc-inclusive">Inclusive Tax</SelectItem>
                  <SelectItem value="exclusive" data-testid="edit-job-calc-exclusive">Exclusive Tax</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
              <Select value={ef.customer_id} onValueChange={(v) => setEf({ ...ef, customer_id: v, vehicle_id: "" })}>
                <SelectTrigger data-testid="edit-job-customer" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select customer"/></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Vehicle</Label>
              <Select value={ef.vehicle_id} onValueChange={(v) => setEf({ ...ef, vehicle_id: v })}>
                <SelectTrigger data-testid="edit-job-vehicle" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder={ef.customer_id ? "Select vehicle" : "Pick customer first"}/></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration} · {v.make} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Complaints / Issue Description</Label>
              <Textarea data-testid="edit-job-complaints" value={ef.complaints} onChange={(e) => setEf(x => ({ ...x, complaints: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={4}/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Assign Mechanic</Label>
              <Select value={ef.mechanic_id} onValueChange={(v) => setEf({ ...ef, mechanic_id: v })}>
                <SelectTrigger data-testid="edit-job-mechanic" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                <SelectContent>
                  {mechanics.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  {ef.mechanic_id && !mechanics.some(m => m.id === ef.mechanic_id) && (
                    <SelectItem value={ef.mechanic_id}>{editJob?.mechanic_name && editJob.mechanic_name !== "-" ? editJob.mechanic_name : "Previously assigned"}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
              <Select value={ef.store_id} onValueChange={(v) => setEf({ ...ef, store_id: v })}>
                <SelectTrigger data-testid="edit-job-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select store"/></SelectTrigger>
                <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.store_type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Next Service Date</Label>
              <Input data-testid="edit-job-next-service-date" type="date" value={ef.next_service_date} onChange={(e) => setEf(x => ({ ...x, next_service_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Odometer (km)</Label>
              <Input data-testid="edit-job-odometer" type="number" min="0" value={ef.odometer} onChange={(e) => setEf(x => ({ ...x, odometer: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Remarks</Label>
              <Textarea data-testid="edit-job-notes" value={ef.notes} onChange={(e) => setEf(x => ({ ...x, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
            </div>

            <div className="border border-border rounded-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-primary">Job Rate (Labour)</div>
                <Button data-testid="edit-add-rate-line-btn" type="button" size="sm" variant="outline" onClick={() => setERateLines(x => [...x, { job_type_id: null, name: "New Service", unit: "Job", labour_rate: 0, qty: 1 }])} className="h-7 rounded-sm uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/> Add</Button>
              </div>
              <div className="mb-2">
                <SearchCombobox endpoint="/job-types" placeholder="Search job rate…" testId="edit-job-jt-search"
                  labelFn={(jt) => `${jt.name} · ₹${jt.labour_rate}${jt.category ? ` · ${jt.category}` : ""}`}
                  onSelect={(jt) => setERateLines(x => [...x, { job_type_id: jt.id, name: jt.name, unit: "Job", labour_rate: jt.labour_rate, qty: 1 }])}/>
              </div>
              {eRateLines.length > 0 && (
                <div className="grid grid-cols-[1fr_56px_80px_56px_80px_28px] gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground pb-1">
                  <span>Service Name</span><span>Unit</span><span className="text-right">Rate</span><span className="text-right">Qty</span><span className="text-right">Total</span><span/>
                </div>
              )}
              {eRateLines.map((l, i) => (
                <div key={i} data-testid={`edit-job-rate-${i}`} className="grid grid-cols-[1fr_56px_80px_56px_80px_28px] gap-2 items-center text-xs border-t border-border py-1.5">
                  <Input data-testid={`edit-job-rate-name-${i}`} value={l.name} onChange={(e) => setERateLines(x => x.map((r, j) => j === i ? { ...r, name: e.target.value } : r))} className="h-7 rounded-sm bg-secondary border-border"/>
                  <Input data-testid={`edit-job-rate-unit-${i}`} value={l.unit || ""} onChange={(e) => setERateLines(x => x.map((r, j) => j === i ? { ...r, unit: e.target.value } : r))} className="h-7 rounded-sm bg-secondary border-border"/>
                  <Input type="number" min="0" data-testid={`edit-job-rate-amt-${i}`} value={l.labour_rate} onChange={(e) => setERateLines(x => x.map((r, j) => j === i ? { ...r, labour_rate: Number(e.target.value) || 0 } : r))} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <Input type="number" min="1" data-testid={`edit-job-rate-qty-${i}`} value={l.qty ?? 1} onChange={(e) => setERateLines(x => x.map((r, j) => j === i ? { ...r, qty: Number(e.target.value) || 1 } : r))} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <span className="text-right font-mono" data-testid={`edit-job-rate-total-${i}`}>{inr((l.labour_rate || 0) * (l.qty || 1))}</span>
                  <button type="button" data-testid={`edit-job-rate-del-${i}`} onClick={() => setERateLines(x => x.filter((_, j) => j !== i))} className="text-destructive justify-self-end"><X className="w-3 h-3"/></button>
                </div>
              ))}
            </div>

            <div className="border border-border rounded-sm p-3">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Parts Used</div>
              <div className="mb-2">
                <SearchCombobox endpoint="/items?scope=all" placeholder="Search parts…" testId="edit-job-item-search"
                  labelFn={(it) => `${it.item_name} · MRP ₹${it.mrp}${it.part_number ? ` · ${it.part_number}` : ""}${it.unit ? ` · ${it.unit}` : ""}`}
                  onSelect={(it) => setEPartLines(x => [...x, { item_id: it.id, name: it.item_name, unit: it.unit || "", qty: 1, price: Number(it.mrp || 0) }])}/>
              </div>
              {ePartLines.map((p, i) => (
                <div key={i} data-testid={`edit-job-part-${i}`} className="grid grid-cols-[1fr_70px_120px_32px] gap-2 items-center text-xs border-t border-border py-1.5">
                  <span className="truncate" title={p.name}>{p.name}{p.unit ? ` · ${p.unit}` : ""}{p.batch_number ? ` · Batch ${p.batch_number}` : ""}</span>
                  <Input type="number" min="1" data-testid={`edit-job-part-qty-${i}`} value={p.qty} onChange={(e) => setEPartLines(x => x.map((r, j) => j === i ? { ...r, qty: Number(e.target.value) || 0 } : r))} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <Input type="number" min="0" data-testid={`edit-job-part-price-${i}`} value={p.price} onChange={(e) => setEPartLines(x => x.map((r, j) => j === i ? { ...r, price: Number(e.target.value) || 0 } : r))} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                  <button type="button" onClick={() => setEPartLines(x => x.filter((_, j) => j !== i))} className="text-destructive justify-self-end"><X className="w-3 h-3"/></button>
                </div>
              ))}
            </div>

            <div className="border border-border rounded-sm p-3" data-testid="edit-job-totals">
              <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Totals</div>
              <div className="space-y-1 text-xs font-mono">
                <div className="flex justify-between"><span className="text-muted-foreground uppercase tracking-wider text-[10px]">Parts</span><span data-testid="edit-total-parts">{inr(editTotals.parts)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground uppercase tracking-wider text-[10px]">Labour</span><span data-testid="edit-total-labour">{inr(editTotals.labour)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground uppercase tracking-wider text-[10px]">{ef.calc_type === "exclusive" ? "GST @ 18%" : "of which GST @ 18%"}</span><span data-testid="edit-total-gst">{inr(editTotals.tax)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-bold"><span className="uppercase tracking-wider text-[10px]">Total</span><span className="text-primary" data-testid="edit-total-grand">{inr(editTotals.grand)}</span></div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button data-testid="save-edit-jobcard" onClick={saveEdit} disabled={!ef.customer_id || !ef.vehicle_id || !ef.complaints || !ef.job_type} className="flex-1 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Update Job Card</Button>
              <Button data-testid="cancel-edit-jobcard" type="button" variant="outline" onClick={() => setEditJob(null)} className="rounded-sm uppercase tracking-[0.15em] text-xs">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print sheet for job card list (hidden except when printing list view) */}
      {!detail && (
        <div className="hidden print:block print:bg-white print:text-black" id="print-jobcards">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-lg font-bold">{org?.business_name || "SynqMoto ERP Workshop"}</div>
              <div className="text-xs">{org?.establishment_name || org?.branch_name || ""}</div>
            </div>
            <div className="text-right text-xs">
              <div className="font-bold">Job Cards Report</div>
              <div>Report Date: {new Date().toISOString().slice(0, 10)}</div>
            </div>
          </div>
          <div className="text-xs mb-3 border-b border-black pb-2">
            Filters — Period: {dateFrom || "*"} to {dateTo || "*"} · Job Type: {jobTypeFilter === "all" ? "All" : jobTypeFilter} · Rows: {filteredJobs.length}
          </div>
          <table className="w-full border-collapse" style={{ fontSize: "8px" }}>
            <thead>
              <tr>
                {["Job Card #", "Date", "Customer", "Mobile", "Vehicle", "Job Type", "Mechanic", "Status", "Parts", "Labour", "Total", "Cash", "PhonePay", "Paytm", "Total", "Balance", "Payment"].map((h, i) => (
                  <th key={`${h}-${i}`} className="border border-black text-left p-1">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(j => (
                <tr key={j.id}>
                  <td className="border border-black p-1">{j.job_number}</td>
                  <td className="border border-black p-1">{j.job_date || (j.created_at || "").slice(0, 10)}</td>
                  <td className="border border-black p-1">{j.customer_name || "-"}</td>
                  <td className="border border-black p-1">{j.customer_phone || "-"}</td>
                  <td className="border border-black p-1">{j.vehicle_reg || j.vehicle_label || "-"}</td>
                  <td className="border border-black p-1">{j.job_type || j.service_type || "-"}</td>
                  <td className="border border-black p-1">{j.mechanic_name || "-"}</td>
                  <td className="border border-black p-1">{j.display_status || j.status}</td>
                  <td className="border border-black p-1 text-right">{inr(j.parts_total || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(j.labour_charges || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr((j.parts_total || 0) + (j.labour_charges || 0))}</td>
                  <td className="border border-black p-1 text-right">{inr(j.paid_cash || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(j.paid_phonepay || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(j.paid_paytm || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(j.amount_paid || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(j.balance_due || 0)}</td>
                  <td className="border border-black p-1">{PAY_STATUS[j.payment_status]?.label || "—"}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={8}>TOTALS · {filteredJobs.length} jobs</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.parts)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.labour)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.amount)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.cash)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.phonepay)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.paytm)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.paid)}</td>
                <td className="border border-black p-1 text-right">{inr(jcTotals.balance)}</td>
                <td className="border border-black p-1"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) { setDetail(null); setPhotoUrls({}); setJobChallans([]); setJobInvoices([]); }}}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto print:hidden">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase flex items-center justify-between gap-4">
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-primary">{detail.job_number}</span>
                    <span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm ${statusColor[detail.status]}`}>{detail.status.replace("_"," ")}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <Button data-testid="minimise-detail-jobcard-btn" size="sm" variant="outline" onClick={minimisePage} className="rounded-sm border-border text-xs uppercase tracking-wider" title="Minimise to taskbar">
                      <Minimize2 className="w-3 h-3 mr-1"/> Minimise
                    </Button>
                    {isStaff && !["delivered", "cancelled"].includes(detail.status) && (
                      <Button data-testid="create-challan-btn" size="sm" variant="outline" onClick={createChallan} className="rounded-sm border-border text-xs uppercase tracking-wider">
                        <FileText className="w-3 h-3 mr-1"/> New Challan
                      </Button>
                    )}
                    <Button data-testid="print-job-btn" size="sm" variant="outline" onClick={printJobSheet} className="rounded-sm border-border text-xs uppercase tracking-wider">
                      <Printer className="w-3 h-3 mr-1"/> Print Sheet
                    </Button>
                    {canEditJob && MODIFY_STATUSES.includes(detail.status) && (
                      <Button data-testid="detail-modify-btn" size="sm" variant="outline" onClick={() => setModifyJob({ ...jobs.find(x => x.id === detail.id), ...detail })} className="rounded-sm border-border text-xs uppercase tracking-wider">
                        <Pencil className="w-3 h-3 mr-1"/> Modify
                      </Button>
                    )}
                    {detail.status === "open" && canEditLines && (
                      <Button data-testid="detail-start-btn" size="sm" onClick={() => setStatus(detail.id, "in_progress")} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">
                        <Play className="w-3 h-3 mr-1"/> Start Job
                      </Button>
                    )}
                    {detail.status === "in_progress" && canEditLines && (
                      <Button data-testid="detail-complete-btn" size="sm" onClick={() => setCompleteJob(detail)} className="rounded-sm bg-green-600 hover:bg-green-700 text-white text-xs uppercase tracking-wider">
                        <CheckCircle className="w-3 h-3 mr-1"/> Complete
                      </Button>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="border border-border rounded-sm p-3 text-sm">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Complaint</div>
                  <div className="whitespace-pre-wrap">{detail.complaints}</div>
                </div>

                <div className="border border-border rounded-sm p-3 text-sm grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Store</div>
                    {isStaff && !["delivered", "cancelled"].includes(detail.status) ? (
                      <Select value={detail.store_id || ""} onValueChange={async (v) => {
                        try {
                          await api.patch(`/jobcards/${detail.id}/store?store_id=${v}`);
                          const { data } = await api.get(`/jobcards/${detail.id}`);
                          setDetail(data); load();
                          toast.success("Store updated");
                        } catch (e) { toast.error(formatErr(e)); }
                      }}>
                        <SelectTrigger data-testid="detail-store" className="rounded-sm bg-secondary border-border"><SelectValue placeholder="Select store"/></SelectTrigger>
                        <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.store_type}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <div data-testid="detail-store-label">{stores.find(s => s.id === detail.store_id)?.name || "—"}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Calculation Type</div>
                    {isStaff && !["delivered", "cancelled"].includes(detail.status) ? (
                      <Select value={detail.calc_type || "inclusive"} onValueChange={async (v) => {
                        try {
                          await api.patch(`/jobcards/${detail.id}/calc-type?calc_type=${v}`);
                          const { data } = await api.get(`/jobcards/${detail.id}`);
                          setDetail(data); load();
                          toast.success("Calculation type updated");
                        } catch (e) { toast.error(formatErr(e)); }
                      }}>
                        <SelectTrigger data-testid="detail-calc-type" className="rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inclusive" data-testid="calc-type-inclusive">Inclusive Tax</SelectItem>
                          <SelectItem value="exclusive" data-testid="calc-type-exclusive">Exclusive Tax</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div data-testid="detail-calc-type-label">{(detail.calc_type || "inclusive") === "inclusive" ? "Inclusive Tax" : "Exclusive Tax"}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Job Date</div>
                    {isStaff && !["delivered", "cancelled"].includes(detail.status) ? (
                      <Input data-testid="detail-job-date" type="date" max={new Date().toISOString().slice(0, 10)} value={detail.job_date || (detail.created_at || "").slice(0, 10)} onChange={async (e) => {
                        const v = e.target.value;
                        if (!v || v === (detail.job_date || "")) return;
                        try {
                          await api.patch(`/jobcards/${detail.id}/date?job_date=${v}`);
                          const { data } = await api.get(`/jobcards/${detail.id}`);
                          setDetail(data); load();
                          toast.success("Job date updated");
                        } catch (err) { toast.error(formatErr(err)); }
                      }} className="rounded-sm bg-secondary border-border"/>
                    ) : (
                      <div data-testid="detail-job-date-label">{detail.job_date || (detail.created_at || "").slice(0, 10)}</div>
                    )}
                  </div>
                </div>

                {!editable && (
                  <div className="border border-yellow-500/40 bg-yellow-500/10 text-yellow-500 p-3 rounded-sm text-xs uppercase tracking-wider">
                    Rate & Parts unlock once the job is In Progress.
                  </div>
                )}

                {editable && (
                  <>
                    {/* Job Rate Section */}
                    <div className="border border-border rounded-sm p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2"><Wrench className="w-3 h-3"/> Job Rate</div>
                        <div className="font-mono text-sm">Subtotal: <span className="text-primary">{inr(detail.labour_charges || 0)}</span></div>
                      </div>
                      <div className="flex gap-2 mb-2">
                        <Select value={jtCat} onValueChange={(v) => { setJtCat(v); setJtGroup(""); }}>
                          <SelectTrigger data-testid="detail-jt-cat" className="rounded-sm bg-secondary border-border flex-1 h-8 text-xs"><SelectValue placeholder="Filter: Category"/></SelectTrigger>
                          <SelectContent>{[...new Set(jobTypes.map(jt => jt.category).filter(Boolean))].sort().map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={jtGroup} onValueChange={(v) => { setJtGroup(v); }}>
                          <SelectTrigger data-testid="detail-jt-group" className="rounded-sm bg-secondary border-border flex-1 h-8 text-xs"><SelectValue placeholder="Filter: Group"/></SelectTrigger>
                          <SelectContent>{[...new Set(jobTypes.filter(jt => !jtCat || jt.category === jtCat).map(jt => jt.group).filter(Boolean))].sort().map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                        </Select>
                        {(jtCat || jtGroup) && (
                          <Button data-testid="detail-jt-clear" type="button" size="sm" variant="ghost" onClick={() => { setJtCat(""); setJtGroup(""); }} className="h-8 text-xs text-muted-foreground"><X className="w-3 h-3"/></Button>
                        )}
                      </div>
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <SearchCombobox endpoint="/job-types" placeholder="Search job rate…" testId="detail-jt-search"
                            labelFn={(jt) => `${jt.name} · ₹${jt.labour_rate}${jt.category ? ` · ${jt.category}` : ""}`}
                            onSelect={(jt) => addRateLineFromMaster(jt)}/>
                        </div>
                        <Button data-testid="add-rate-line-btn" type="button" size="sm" variant="outline" onClick={addManualRateLine} className="rounded-sm uppercase tracking-[0.15em] text-xs h-8"><Plus className="w-3 h-3 mr-1"/> Add</Button>
                      </div>
                      {(detail.rate_lines || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-3">No job rates added yet</div>
                      ) : (
                        <div>
                          <div className="grid grid-cols-[1fr_64px_90px_60px_90px_28px] gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border pb-1 mb-1">
                            <span>Service Name</span><span>Unit</span><span className="text-right">Rate</span><span className="text-right">Qty</span><span className="text-right">Total</span><span/>
                          </div>
                          {(detail.rate_lines || []).map((rl, i) => (
                            <div key={i} data-testid={`rate-line-${i}`} className="grid grid-cols-[1fr_64px_90px_60px_90px_28px] gap-2 items-center border-b border-border py-1.5 text-sm">
                              <Input data-testid={`rate-name-${i}`} value={rl.name} onChange={(e) => setRlLocal(i, { name: e.target.value })} onBlur={(e) => updateRateLine(i, { name: e.target.value })} className="h-7 rounded-sm bg-secondary border-border"/>
                              <Input data-testid={`rate-unit-${i}`} value={rl.unit || ""} onChange={(e) => setRlLocal(i, { unit: e.target.value })} onBlur={(e) => updateRateLine(i, { unit: e.target.value })} className="h-7 rounded-sm bg-secondary border-border"/>
                              <Input type="number" min="0" data-testid={`rate-amt-${i}`} value={rl.labour_rate} onChange={(e) => setRlLocal(i, { labour_rate: Number(e.target.value) || 0 })} onBlur={(e) => updateRateLine(i, { labour_rate: Number(e.target.value) || 0 })} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                              <Input type="number" min="1" data-testid={`rate-qty-${i}`} value={rl.qty ?? 1} onChange={(e) => setRlLocal(i, { qty: Number(e.target.value) || 1 })} onBlur={(e) => updateRateLine(i, { qty: Number(e.target.value) || 1 })} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                              <span className="text-right font-mono" data-testid={`rate-total-${i}`}>{inr((rl.labour_rate || 0) * (rl.qty || 1))}</span>
                              <Button data-testid={`del-rate-${i}`} size="sm" variant="ghost" onClick={() => removeRateLine(i)} className="h-7 w-7 p-0 text-destructive justify-self-end"><Trash2 className="w-3 h-3"/></Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Parts Used Section */}
                    <div className="border border-border rounded-sm p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2"><Package className="w-3 h-3"/> Parts Used</div>
                        <div className="font-mono text-sm">Subtotal: <span className="text-primary">{inr(detail.parts_total || 0)}</span></div>
                      </div>
                      <div className="mb-3">
                        <SearchCombobox endpoint="/items?scope=all" placeholder="Search parts (Item Master)…" testId="detail-part-search"
                          labelFn={(it) => `${it.item_name} · MRP ₹${it.mrp}${it.part_number ? ` · ${it.part_number}` : ""}${it.unit ? ` · ${it.unit}` : ""}`}
                          onSelect={(it) => addPartFromMaster(it)}/>
                      </div>
                      {(detail.parts || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-3">No parts added yet</div>
                      ) : (
                        <div>
                          <div className="grid grid-cols-[1fr_64px_90px_60px_90px_28px] gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border pb-1 mb-1">
                            <span>Part Name</span><span>Unit</span><span className="text-right">Rate</span><span className="text-right">Qty</span><span className="text-right">Total</span><span/>
                          </div>
                          {(detail.parts || []).map((p, i) => (
                            <div key={i} data-testid={`part-line-${i}`} className="grid grid-cols-[1fr_64px_90px_60px_90px_28px] gap-2 items-center border-b border-border py-1.5 text-sm">
                              <span className="truncate" title={p.name}>{p.name}</span>
                              <span className="text-muted-foreground text-xs" data-testid={`part-unit-${i}`}>{p.unit || "—"}</span>
                              <Input type="number" min="0" step="any" data-testid={`part-rate-${i}`} value={p.price} onChange={(e) => setPartLocal(i, { price: Number(e.target.value) || 0 })} onBlur={(e) => updatePartLineRate(i, Number(e.target.value) || 0)} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                              <Input type="number" min="1" data-testid={`part-qty-${i}`} value={p.qty} onChange={(e) => setPartLocal(i, { qty: Number(e.target.value) || 1 })} onBlur={(e) => updatePartLine(i, Number(e.target.value) || 1)} className="h-7 text-right font-mono rounded-sm bg-secondary border-border"/>
                              <span className="text-right font-mono" data-testid={`part-total-${i}`}>{inr((p.qty || 0) * (p.price || 0))}</span>
                              <Button data-testid={`del-part-${i}`} size="sm" variant="ghost" onClick={() => removePartLine(i)} className="h-7 w-7 p-0 text-destructive justify-self-end"><Trash2 className="w-3 h-3"/></Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <JobTotals detail={detail} />
                  </>
                )}

                {editable && <FollowUpSection detail={detail} onSaved={(patch) => { setDetail(d => ({ ...d, ...patch })); load(); }}/>}

                {/* Notes */}
                <div className="border border-border rounded-sm p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3 flex items-center gap-2"><MessageSquare className="w-3 h-3"/> Notes</div>
                  <div className="flex gap-2 mb-3">
                    <Input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note (e.g. 'ordered replacement chain')" className="rounded-sm bg-secondary border-border flex-1" onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} />
                    <Button data-testid="add-note-btn" onClick={addNote} disabled={!noteText.trim()} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">
                      <Plus className="w-3 h-3 mr-1"/> Add
                    </Button>
                  </div>
                  {(detail.note_log || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-2">No notes yet</div>
                  ) : (
                    <div className="space-y-2">
                      {(detail.note_log || []).map(n => (
                        <div key={n.id} className="border-l-2 border-primary/40 pl-3 py-1 text-sm flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div>{n.text}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{n.by} · {n.role} · {new Date(n.created_at).toLocaleString()}</div>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => delNote(n.id)} className="h-6 w-6 p-0 text-destructive"><X className="w-3 h-3"/></Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Photos */}
                <div className="border border-border rounded-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2"><Camera className="w-3 h-3"/> Photos</div>
                    <label className="cursor-pointer">
                      <input data-testid="photo-input" type="file" accept="image/*" multiple onChange={(e) => uploadPhoto(e.target.files)} className="hidden"/>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">
                        <Plus className="w-3 h-3"/> {photoUploading ? "Uploading..." : "Add Photo"}
                      </span>
                    </label>
                  </div>
                  {(detail.photos || []).filter(p => !p.is_deleted).length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-3">No photos yet</div>
                  ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {(detail.photos || []).filter(p => !p.is_deleted).map(p => (
                        <div key={p.id} className="relative group aspect-square bg-secondary rounded-sm overflow-hidden">
                          {photoUrls[p.id] ? (
                            <img src={photoUrls[p.id]} alt="job" className="w-full h-full object-cover"/>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">Loading…</div>
                          )}
                          <button onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 p-1 rounded-sm bg-destructive/90 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3 text-white"/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Challans Section */}
                <div className="border border-border rounded-sm p-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 mb-2"><FileText className="w-3 h-3"/> Challans</div>
                  {jobChallans.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-3">No challans yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary text-[10px] uppercase tracking-widest text-muted-foreground">
                          <tr>
                            <th className="text-left p-2">Challan #</th>
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Items</th>
                            <th className="text-right p-2">Total</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-right p-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobChallans.map(c => (
                            <tr key={c.id} data-testid={`jc-challan-${c.id}`} className="border-t border-border">
                              <td className="p-2 font-mono text-primary">
                                <button data-testid={`jc-ch-view-${c.id}`} onClick={() => nav(`/challans`)} className="hover:underline">{c.challan_number}</button>
                              </td>
                              <td className="p-2 font-mono">{c.date}</td>
                              <td className="p-2">{(c.rate_lines || []).length + (c.parts || []).length}</td>
                              <td className="p-2 text-right font-mono">{inr(c.total)}</td>
                              <td className="p-2"><span className={`px-1.5 py-0.5 text-[9px] uppercase rounded-sm border ${c.status === "Converted to Invoice" ? "text-green-500 bg-green-500/10 border-green-500/40" : c.status === "Issued" ? "text-blue-400 bg-blue-500/10 border-blue-500/40" : "text-muted-foreground bg-secondary border-border"}`}>{c.status}</span></td>
                              <td className="p-2 text-right space-x-1 whitespace-nowrap">
                                {c.status !== "Converted to Invoice" && (
                                  <Button data-testid={`jc-ch-convert-${c.id}`} size="sm" variant="ghost" onClick={() => convertChallan(c)} className="h-6 text-[10px] text-green-500" title="Convert to Invoice"><FileText className="w-3 h-3"/></Button>
                                )}
                                <Button data-testid={`jc-ch-pdf-${c.id}`} size="sm" variant="ghost" onClick={() => fetchDocPdf("challan", c.id, c.challan_number, true)} className="h-6 w-6 p-0" title="Download PDF"><FileDown className="w-3 h-3"/></Button>
                                <Button data-testid={`jc-ch-print-${c.id}`} size="sm" variant="ghost" onClick={() => fetchDocPdf("challan", c.id, c.challan_number, false)} className="h-6 w-6 p-0" title="Print"><Printer className="w-3 h-3"/></Button>
                                {canEditJob && c.status !== "Converted to Invoice" && (
                                  <Button data-testid={`jc-ch-edit-${c.id}`} size="sm" variant="ghost" onClick={() => nav(`/challans`)} className="h-6 w-6 p-0" title="Edit"><Pencil className="w-3 h-3"/></Button>
                                )}
                                {canDeleteJob && c.status !== "Converted to Invoice" && (
                                  <Button data-testid={`jc-ch-del-${c.id}`} size="sm" variant="ghost" onClick={() => deleteChallan(c)} className="h-6 w-6 p-0 text-destructive" title="Delete"><Trash2 className="w-3 h-3"/></Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Invoices Section */}
                <div className="border border-border rounded-sm p-3">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 mb-2"><FileText className="w-3 h-3"/> Invoices</div>
                  {jobInvoices.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-3">No invoices yet</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary text-[10px] uppercase tracking-widest text-muted-foreground">
                          <tr>
                            <th className="text-left p-2">Invoice #</th>
                            <th className="text-left p-2">Date</th>
                            <th className="text-right p-2">Labour</th>
                            <th className="text-right p-2">Parts</th>
                            <th className="text-right p-2">GST</th>
                            <th className="text-right p-2">Total</th>
                            <th className="text-left p-2">Payment</th>
                            <th className="text-right p-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobInvoices.map(i => {
                            const partsTotal = (i.parts || []).reduce((s, p) => s + (p.qty || 0) * (p.price || 0), 0);
                            const revoked = i.status === "revoked";
                            return (
                              <tr key={i.id} data-testid={`jc-invoice-${i.id}`} className={`border-t border-border ${revoked ? "opacity-60" : ""}`}>
                                <td className="p-2 font-mono text-primary">
                                  <button data-testid={`jc-inv-view-${i.id}`} onClick={() => nav(`/invoices`)} className="hover:underline">{i.invoice_number}</button>
                                </td>
                                <td className="p-2 font-mono">{(i.created_at || "").slice(0, 10)}</td>
                                <td className="p-2 text-right font-mono">{inr(i.labour_charges || 0)}</td>
                                <td className="p-2 text-right font-mono">{inr(partsTotal)}</td>
                                <td className="p-2 text-right font-mono">{inr(i.gst_amount || 0)}</td>
                                <td className="p-2 text-right font-mono font-semibold">{inr(i.total || 0)}</td>
                                <td className="p-2"><span className={`px-1.5 py-0.5 text-[9px] uppercase rounded-sm border ${revoked ? "text-red-500 bg-red-500/10 border-red-500/40" : i.status === "paid" ? "text-green-500 bg-green-500/10 border-green-500/40" : i.status === "partial" ? "text-yellow-500 bg-yellow-500/10 border-yellow-500/40" : "text-muted-foreground bg-secondary border-border"}`}>{i.status}</span></td>
                                <td className="p-2 text-right space-x-1 whitespace-nowrap">
                                  <Button data-testid={`jc-inv-pdf-${i.id}`} size="sm" variant="ghost" onClick={() => fetchDocPdf("invoice", i.id, i.invoice_number, true)} className="h-6 w-6 p-0" title="Download PDF"><FileDown className="w-3 h-3"/></Button>
                                  <Button data-testid={`jc-inv-print-${i.id}`} size="sm" variant="ghost" onClick={() => fetchDocPdf("invoice", i.id, i.invoice_number, false)} className="h-6 w-6 p-0" title="Print"><Printer className="w-3 h-3"/></Button>
                                  {!revoked && canEditJob && (
                                    <Button data-testid={`jc-inv-edit-${i.id}`} size="sm" variant="ghost" onClick={() => nav(`/invoices`)} className="h-6 w-6 p-0" title="Edit"><Pencil className="w-3 h-3"/></Button>
                                  )}
                                  {!revoked && canDeleteJob && !i.paid && (
                                    <Button data-testid={`jc-inv-del-${i.id}`} size="sm" variant="ghost" onClick={() => deleteInvoice(i)} className="h-6 w-6 p-0 text-destructive" title="Delete"><Trash2 className="w-3 h-3"/></Button>
                                  )}
                                  {!revoked && canDeleteJob && (
                                    <Button data-testid={`jc-inv-revoke-${i.id}`} size="sm" variant="ghost" onClick={() => revokeInvoice(i)} className="h-6 text-[10px] text-red-500" title="Revoke Invoice"><X className="w-3 h-3 mr-0.5"/>Revoke</Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Print sheet (hidden except when printing) */}
      {detail && (
        <div className="hidden print:block print:bg-white print:text-black p-6" id="print-job-sheet">
          <div className="border-b-2 border-black pb-3 mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest">SynqMoto ERP Workshop</div>
              <div className="text-2xl font-bold">JOB SHEET · {detail.job_number}</div>
            </div>
            <div className="text-right text-xs">
              <div>Date: {new Date(detail.created_at).toLocaleDateString()}</div>
              <div>Job Type: {detail.job_type || detail.service_type || "-"}</div>
              <div>Status: <span className="uppercase">{detail.status.replace("_"," ")}</span></div>
            </div>
          </div>
          <div className="mb-4">
            <div className="text-xs uppercase font-bold border-b border-black pb-1 mb-2">Complaint</div>
            <div className="whitespace-pre-wrap text-sm">{detail.complaints}</div>
          </div>
          <table className="w-full text-sm mb-4">
            <thead><tr className="border-b border-black"><th className="text-left py-1">Job Rate</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Rate</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody>
              {(detail.rate_lines || []).map((rl, i) => (
                <tr key={i} className="border-b border-gray-300"><td className="py-1">{rl.name}</td><td className="text-right">{rl.qty || 1}</td><td className="text-right">₹{rl.labour_rate}</td><td className="text-right">₹{(rl.labour_rate || 0) * (rl.qty || 1)}</td></tr>
              ))}
              <tr className="font-bold"><td colSpan={3} className="py-1">Labour Subtotal</td><td className="text-right">₹{detail.labour_charges || 0}</td></tr>
            </tbody>
          </table>
          <table className="w-full text-sm mb-4">
            <thead><tr className="border-b border-black"><th className="text-left py-1">Parts Used</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Rate</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody>
              {(detail.parts || []).map((p, i) => (
                <tr key={i} className="border-b border-gray-300"><td className="py-1">{p.name}</td><td className="text-right">{p.qty}</td><td className="text-right">₹{p.price}</td><td className="text-right">₹{p.qty * p.price}</td></tr>
              ))}
              <tr className="font-bold"><td colSpan={3} className="py-1">Parts Subtotal</td><td className="text-right">₹{detail.parts_total || 0}</td></tr>
            </tbody>
          </table>
          <div className="text-right border-t-2 border-black pt-2 mb-8">
            <div className="text-xs mb-1">Calculation Type: {jobTaxInfo(detail).ct === "inclusive" ? "Inclusive Tax" : "Exclusive Tax"}</div>
            <div className="text-xs uppercase">{jobTaxInfo(detail).ct === "inclusive" ? `Includes GST @ ${jobTaxInfo(detail).rate}%` : `GST @ ${jobTaxInfo(detail).rate}%`}: ₹{Math.round(jobTaxInfo(detail).tax * 100) / 100}</div>
            <div className="text-xs uppercase">Grand Total{jobTaxInfo(detail).ct === "inclusive" ? " (incl. tax)" : ""}</div>
            <div className="text-2xl font-bold">₹{Math.round(jobTaxInfo(detail).grand * 100) / 100}</div>
          </div>
          <div className="grid grid-cols-2 gap-8 mt-12">
            <div className="border-t border-black pt-1 text-xs text-center">Mechanic Signature</div>
            <div className="border-t border-black pt-1 text-xs text-center">Customer Signature</div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page { size: A5; margin: 10mm; }
          body > *:not(#print-job-sheet) { display: none !important; }
          #print-job-sheet { display: block !important; }
        }
      `}</style>
    </div>
  );
}
